import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Car, MapPin, LogOut, Plus, Package, CreditCard, Loader2, X, Edit, Trash2, Users, ChevronDown, ChevronUp, BarChart3, TrendingUp, DollarSign, FileText, Building2, Camera, AlertTriangle, Menu, Navigation, ClipboardCheck, Truck } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ClientDashboardHome from '@/components/ClientDashboardHome';
import ClientSidebar from '@/components/ClientSidebar';
import CompanyProfile from '@/components/CompanyProfile';
import { ResponsiveGrid, ResponsiveCard, ResponsiveTable, ResponsiveFormGrid, MobileActionButton } from '@/components/ResponsiveLayout';
import ClientDeliveriesTab from '@/components/client/ClientDeliveriesTab';
import ClientInspectionsTab from '@/components/client/ClientInspectionsTab';
import ClientGPSLiveTab from '@/components/client/ClientGPSLiveTab';
import OrderServiceDocumentsField, { uploadOrderServiceDocuments } from '@/components/OrderServiceDocumentsField';
import ClientOrderDocumentsList from '@/components/ClientOrderDocumentsList';
import { fetchAllOrdersPaged } from '@/api/client';
import {
  translateAdditionalServiceName,
  translateAdditionalServiceDescription,
} from '@/utils/additionalServiceDisplayName';
import { buildContractPdfFilename } from '@/utils/contractDownload';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import { calculateDrivingDistance } from '@/utils/distance';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Keeps calendar above Google Places (.pac-container) and anchored in scrollable/modal UIs (Floating UI fixed). */
const DATE_PICKER_POPPER_PROPS = {
  popperProps: { strategy: 'fixed' },
};

// Safe fetch wrapper to prevent "Response body already used" errors
const safeFetch = async (url, options = {}) => {
  const response = await fetch(url, options);
  // Clone the response immediately to avoid body consumption issues
  const clonedResponse = response.clone();
  
  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    json: async () => {
      try {
        return await clonedResponse.json();
      } catch (e) {
        // If clone fails, try original
        return await response.json();
      }
    },
    text: async () => {
      try {
        return await clonedResponse.text();
      } catch (e) {
        return await response.text();
      }
    }
  };
};

/** Normalize "a | b" and "a|b" from backend vs UI. */
function normalizeMarginTimeString(marginTime) {
  if (!marginTime) return '';
  return String(marginTime).trim().replace(/\s*\|\s*/g, '|');
}

/**
 * Margin strings may be "date | time", time-only (e.g. "16:00 - 17:00"), or date-only.
 * Falls back to structured order fields when the margin string does not carry both parts.
 */
function resolveMarginDateTimeDisplay(marginTime, dateFrom, dateTo, timeStart, timeEnd, dateLocale, t, calendarFallbackIso) {
  const na = () => t('client_date_not_specified');
  const normalized = normalizeMarginTimeString(marginTime);

  let datePart = null;
  let timePart = null;
  if (normalized) {
    if (normalized.includes('|')) {
      const parts = normalized.split('|').map((x) => x.trim());
      datePart = parts[0] || null;
      timePart = parts[1] || null;
    } else if (/^\d{1,2}:\d{2}/.test(normalized)) {
      timePart = normalized;
    } else {
      datePart = normalized;
    }
  }

  const fmtDate = (d) => {
    if (!d) return null;
    try {
      const parsed = new Date(d);
      if (Number.isNaN(parsed.getTime())) return String(d);
      return parsed.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return String(d);
    }
  };

  if (!datePart && (dateFrom || dateTo)) {
    if (dateFrom && dateTo && String(dateFrom) !== String(dateTo)) {
      const a = fmtDate(dateFrom);
      const b = fmtDate(dateTo);
      datePart = a && b ? `${a} - ${b}` : a || b;
    } else {
      datePart = fmtDate(dateFrom || dateTo);
    }
  }

  if (!datePart && calendarFallbackIso) {
    datePart = fmtDate(calendarFallbackIso);
  }

  if (!timePart && (timeStart || timeEnd)) {
    timePart = timeStart && timeEnd ? `${timeStart} - ${timeEnd}` : timeStart || timeEnd || null;
  }

  return {
    date: datePart || na(),
    time: timePart || na(),
  };
}

function clientVisibleOrderStatus(order) {
  const st = order.status;
  const hasDriver = order.driver_id || order.driver_name;
  if (hasDriver && (st === 'pending' || st === 'confirmed' || st === 'por_asignar')) return 'assigned';
  return st;
}

function translateClientPaymentStatus(paymentStatus, t) {
  if (!paymentStatus) return t('client_date_not_specified');
  if (paymentStatus === 'paid') return t('paid');
  if (paymentStatus === 'pending') return t('payment_status_pending');
  if (paymentStatus === 'failed') return t('failed');
  if (paymentStatus === 'invoiced') return t('client_invoice_status_invoiced');
  return t(paymentStatus, { defaultValue: paymentStatus });
}

// Helper function to get emoji for additional services
const getServiceEmoji = (serviceName) => {
  if (!serviceName) return '➕'; // Return default if serviceName is undefined or null
  
  // If serviceName is an object, extract the name property
  if (typeof serviceName === 'object' && serviceName.name) {
    serviceName = serviceName.name;
  }
  
  // If still not a string, return default
  if (typeof serviceName !== 'string') return '➕';
  
  const lowerName = serviceName.toLowerCase();
  
  // Limpieza
  if (lowerName.includes('limpieza exterior') || lowerName.includes('exterior')) return '💦';
  if (lowerName.includes('limpieza interior') || lowerName.includes('interior')) return '🧹';
  if (lowerName.includes('limpieza')) return '✨';
  
  // ITV
  if (lowerName.includes('itv') || lowerName.includes('inspección técnica')) return '🔍✅';
  
  // PIT STOP
  if (lowerName.includes('pit stop') || lowerName.includes('pit-stop') || lowerName.includes('mantenimiento')) return '🛠️';
  
  // Recuperación
  if (lowerName.includes('recuperación') || lowerName.includes('recuperacion')) {
    if (lowerName.includes('europa') || lowerName.includes('continental')) return '🛡️🗺️';
    if (lowerName.includes('isla') || lowerName.includes('islas') || lowerName.includes('canarias') || lowerName.includes('baleares')) return '🛡️🏝️';
    if (lowerName.includes('ceuta') || lowerName.includes('melilla') || lowerName.includes('áfrica') || lowerName.includes('africa')) return '🛡️🌍';
    return '🛡️'; // Default recuperación
  }
  
  // Representación
  if (lowerName.includes('representación') || lowerName.includes('representacion') || lowerName.includes('verificación') || lowerName.includes('verificacion')) return '🔍📋';
  
  // Default
  return '➕';
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard'); // New: dashboard, services, create
  const [servicesSubTab, setServicesSubTab] = useState('active'); // 'active' or 'historical'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile menu state
  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [filters, setFilters] = useState({
    searchPlate: '',
    paymentStatus: 'all',
    orderStatus: 'all',
    serviceType: 'all',
    city: 'all',
    dateFrom: null,
    dateTo: null
  });
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [expandedIncidents, setExpandedIncidents] = useState(new Set());
  const [incidentDetails, setIncidentDetails] = useState({});
  const [loadingIncident, setLoadingIncident] = useState(null);
  const clientInitialOrdersLoaded = useRef(false);
  
  // Vehicle photos and expenses modal states
  const [showPhotosDialog, setShowPhotosDialog] = useState(false);
  const [showExpensesDialog, setShowExpensesDialog] = useState(false);
  const [showCompletionDetailsDialog, setShowCompletionDetailsDialog] = useState(false);
  const [selectedOrderForView, setSelectedOrderForView] = useState(null);
  const [vehiclePhotos, setVehiclePhotos] = useState(null);
  const [orderExpenses, setOrderExpenses] = useState(null);
  const [completionDetails, setCompletionDetails] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [loadingCompletionDetails, setLoadingCompletionDetails] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const contractPdfDownloadLockRef = useRef(false);
  const [downloadingContractOrderId, setDownloadingContractOrderId] = useState(null);

  // Helper function to get file extension from URL
  const getFileExtension = (url) => {
    if (!url) return 'jpg';
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.pdf') || urlLower.includes('pdf')) return 'pdf';
    if (urlLower.includes('.png')) return 'png';
    if (urlLower.includes('.jpeg') || urlLower.includes('.jpg')) return 'jpg';
    if (urlLower.includes('.gif')) return 'gif';
    if (urlLower.includes('.webp')) return 'webp';
    return 'jpg'; // default
  };

  // Helper function to download file with correct extension
  const downloadFile = (url, fileName) => {
    const extension = getFileExtension(url);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.${extension}`;
    link.click();
  };

  const handleDownloadContract = async (orderId) => {
    if (!orderId || contractPdfDownloadLockRef.current) return;
    contractPdfDownloadLockRef.current = true;
    setDownloadingContractOrderId(orderId);
    try {
      const token = localStorage.getItem('session_token');

      if (!token) {
        toast.error(t('client_toast_no_session'));
        return;
      }

      toast.info(t('client_toast_preparing_contract'));

      const response = await fetch(
        `${BACKEND_URL}/api/admin/orders/${orderId}/generate-contract?_=${Date.now()}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
      );

      if (response.ok) {
        const renderer = response.headers.get('X-AutoGo-Contract-Renderer');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildContractPdfFilename(orderId);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('service_toast_contract_downloaded'));
        if (renderer === 'reportlab') {
          toast.warning(t('service_toast_contract_fallback_simple_pdf'));
        }
      } else {
        let message = t('client_toast_error_download_contract');
        try {
          const err = await response.json();
          if (typeof err.detail === 'string') message = err.detail;
        } catch (_) {
          /* ignore */
        }
        toast.error(message);
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error(t('client_toast_error_download_contract'));
    } finally {
      contractPdfDownloadLockRef.current = false;
      setDownloadingContractOrderId(null);
    }
  };

  // Toggle incident details expansion
  const toggleIncidentExpansion = async (orderId) => {
    const newExpanded = new Set(expandedIncidents);
    
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
      setExpandedIncidents(newExpanded);
    } else {
      newExpanded.add(orderId);
      setExpandedIncidents(newExpanded);
      
      // Fetch incident details if not already loaded
      if (!incidentDetails[orderId]) {
        setLoadingIncident(orderId);
        try {
          const token = localStorage.getItem('session_token');
          const response = await fetch(`${BACKEND_URL}/api/clients/orders/${orderId}/incident-summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            setIncidentDetails(prev => ({ ...prev, [orderId]: data }));
          } else {
            setIncidentDetails(prev => ({ ...prev, [orderId]: { has_incident: false, message: t('client_incident_fetch_failed') } }));
          }
        } catch (error) {
          console.error('Error fetching incident details:', error);
          setIncidentDetails(prev => ({ ...prev, [orderId]: { has_incident: false, message: t('client_incident_fetch_error') } }));
        } finally {
          setLoadingIncident(null);
        }
      }
    }
  };

  const [orderForm, setOrderForm] = useState({
    service_type: 'delivery',
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    pickup_contact_name: '',
    pickup_contact_phone: '',
    pickup_date_start: null,
    pickup_date_end: null,
    pickup_time_start: '',
    pickup_time_end: '',
    delivery_address: '',
    delivery_lat: null,
    delivery_lng: null,
    delivery_contact_name: '',
    delivery_contact_phone: '',
    delivery_date_start: null,
    delivery_date_end: null,
    delivery_time_start: '',
    delivery_time_end: '',
    // Point C for B-C service (second delivery)
    point_c_address: '',
    point_c_lat: null,
    point_c_lng: null,
    point_c_contact_name: '',
    point_c_contact_phone: '',
    point_c_date_start: null,
    point_c_date_end: null,
    point_c_time_start: '',
    point_c_time_end: '',
    car_type: '',
    car_brand: '',
    car_model: '',
    car_plate: '',
    fuel_type: '',
    has_second_vehicle: false,
    second_car_type: '',
    second_car_brand: '',
    second_car_model: '',
    second_car_plate: '',
    second_fuel_type: '',
    distance_km: 0,
    base_price: 0,
    // For single vehicle (delivery) or when not B-C/Trade-In
    selected_additional_services: [],
    notes: '',
    // Representación specific fields
    buyer_name: '',
    buyer_surname: '',
    buyer_phone: '',
    buyer_dni: '',
    seller_name: '',
    seller_surname: '',
    seller_phone: '',
    seller_dni: '',
    verification_address: '',
    verification_lat: null,
    verification_lng: null,
    verification_date: null,
    verification_time_start: '',
    verification_time_end: '',
    // For B-C and Trade-In: separate services and notes per vehicle
    first_vehicle_additional_services: [],
    first_vehicle_notes: '',
    second_vehicle_additional_services: [],
    second_vehicle_notes: ''
  });
  const [orderAttachmentFiles, setOrderAttachmentFiles] = useState([]);
  const [orderClientDocsRefresh, setOrderClientDocsRefresh] = useState(0);

  const [pricing, setPricing] = useState({
    delivery_price: 0,
    bc_service_price: 0,
    tradein_price: 0
  });

  // Control if using fixed date/time or range for different sections
  const [useFixedDateTime, setUseFixedDateTime] = useState(false); // For Representación
  const [useFixedPickupDateTime, setUseFixedPickupDateTime] = useState(false); // For Pickup
  const [useFixedDeliveryDateTime, setUseFixedDeliveryDateTime] = useState(false); // For Delivery
  const [useFixedPointCDateTime, setUseFixedPointCDateTime] = useState(false); // For Point C

  // Function to get fuel type icon
  const getFuelIcon = (fuelType) => {
    const type = fuelType?.toLowerCase();
    switch(type) {
      case 'gasoline':
      case 'gasolina':
        return '⛽'; // Gasoline pump
      case 'diesel':
      case 'diésel':
        return '⛽'; // Gasoline pump (same as gasoline)
      case 'electric':
      case 'eléctrico':
        return '⚡'; // Lightning bolt for electric
      case 'hybrid':
      case 'híbrido':
        return '⛽⚡'; // Gasoline + Lightning for hybrid
      default:
        return '🚗'; // Default car icon
    }
  };

  // Refresh user data from backend
  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Clone response to prevent "body already used" errors from monitoring scripts
        const responseClone = response.clone();
        let userData;
        try {
          userData = await response.json();
        } catch (e) {
          userData = await responseClone.json();
        }
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('✅ User data refreshed:', userData);
        console.log('💳 Payment type:', userData.payment_type);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    setLoading(true);
    refreshUserData();
    loadData().finally(() => {
      clientInitialOrdersLoaded.current = true;
    });
  }, [navigate]);

  useEffect(() => {
    loadGoogleMaps().catch((err) => {
      console.warn('Google Maps failed to load:', err?.message || err);
    });
  }, []);

  // Refresh when returning to the services tab (skip duplicate right after initial mount)
  useEffect(() => {
    if (activeTab !== 'services' || !clientInitialOrdersLoaded.current) return;
    loadData();
  }, [activeTab]);

  // Scroll to top when switching to services tab
  useEffect(() => {
    if (activeTab === 'services') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': 'Bearer ' + token };

      const [ordersData, servicesRes] = await Promise.all([
        fetchAllOrdersPaged({ headers, pageSize: 500 }).catch((err) => {
          console.error('Error loading orders:', err);
          return null;
        }),
        fetch(BACKEND_URL + '/api/additional-services', { headers })
      ]);

      if (ordersData != null) {
        setOrders(ordersData);
      }

      if (servicesRes.ok) {
        const servicesClone = servicesRes.clone();
        let servicesData;
        try {
          servicesData = await servicesRes.json();
        } catch (e) {
          servicesData = await servicesClone.json();
        }
        setAdditionalServices(servicesData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/client/billing-periods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const responseClone = response.clone();
        let data;
        try {
          data = await response.json();
        } catch (e) {
          data = await responseClone.json();
        }
        setInvoices(data || []);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoadingInvoices(false);
    }
  };


  const handleLogout = () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const loadVehiclePhotos = async (orderId) => {
    setLoadingPhotos(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/client/orders/${orderId}/vehicle-photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Check if photos are available or if it's a pending message
        if (data.message && data.status && data.status !== 'approved') {
          // Photos not available yet, show informative message
          setVehiclePhotos({
            pending: true,
            status: data.status,
            message: data.message,
            note: data.note
          });
          if (data.status === 'pending_approval') {
            toast.info(t('client_toast_photos_pending_approval'));
          } else {
            toast.info(t('client_toast_photos_not_available'));
          }
        } else {
          setVehiclePhotos(data);
        }
      } else {
        toast.error(data.detail || data.message || t('client_toast_no_photos_yet'));
        setVehiclePhotos(null);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error(t('client_toast_error_load_photos'));
    } finally {
      setLoadingPhotos(false);
    }
  };

  const loadOrderExpenses = async (orderId) => {
    setLoadingExpenses(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/client/orders/${orderId}/expenses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setOrderExpenses(data);
      } else {
        toast.error(data.message || t('client_toast_no_expenses_yet'));
        setOrderExpenses(null);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error(t('client_toast_error_load_expenses'));
    } finally {
      setLoadingExpenses(false);
    }
  };

  const loadCompletionDetails = async (orderId) => {
    setLoadingCompletionDetails(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/client/orders/${orderId}/completion-details`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCompletionDetails(data);
      } else {
        setCompletionDetails(null);
      }
    } catch (error) {
      console.error('Error loading completion details:', error);
      toast.error(t('client_toast_error_load_completion'));
    } finally {
      setLoadingCompletionDetails(false);
    }
  };

  const calculateDistance = async () => {
    if (!orderForm.pickup_lat || !orderForm.delivery_lat) {
      toast.error(t('address_required'));
      return;
    }

    // For B-C service, also need point C coordinates
    if (orderForm.service_type === 'bc_service' && (!orderForm.point_c_lat || !orderForm.point_c_lng)) {
      toast.error(t('point_c_required'));
      return;
    }

    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps not loaded, using Haversine distance');
    }

    setCalculatingDistance(true);
    try {
      let totalDistance = 0;
      let distanceAB = 0;
      let distanceBC = 0;
      
      // Calculate first leg: A to B (always present) - USE DRIVING DISTANCE
      distanceAB = await calculateDrivingDistance(
        orderForm.pickup_lat,
        orderForm.pickup_lng,
        orderForm.delivery_lat,
        orderForm.delivery_lng
      );
      totalDistance += distanceAB;

      // For B-C service: add B to C distance - USE DRIVING DISTANCE
      if (orderForm.service_type === 'bc_service') {
        distanceBC = await calculateDrivingDistance(
          orderForm.delivery_lat,
          orderForm.delivery_lng,
          orderForm.point_c_lat,
          orderForm.point_c_lng
        );
        totalDistance += distanceBC;
      }
      
      // For Trade-In: add B to A distance (return) - USE DRIVING DISTANCE
      if (orderForm.service_type === 'tradein') {
        const distanceBA = await calculateDrivingDistance(
          orderForm.delivery_lat,
          orderForm.delivery_lng,
          orderForm.pickup_lat,
          orderForm.pickup_lng
        );
        totalDistance += distanceBA;
      }

      const distanceInKm = totalDistance.toFixed(2);
      setOrderForm(prev => ({ ...prev, distance_km: parseFloat(distanceInKm) }));
      
      // Build pricing API URL with separate distances for B-C and Trade-In
      const token = localStorage.getItem('session_token');
      let pricingUrl = `${BACKEND_URL}/api/pricing/calculate?distance_km=${distanceInKm}&service_type=${orderForm.service_type}`;
      
      // Add client_id to get personalized pricing
      if (user && user.id) {
        pricingUrl += `&client_id=${user.id}`;
      }
      
      // Add separate distances for B-C service
      if (orderForm.service_type === 'bc_service' && distanceBC > 0) {
        pricingUrl += `&distance_ab=${distanceAB.toFixed(2)}&distance_bc=${distanceBC.toFixed(2)}`;
      }
      
      // Add separate distance for Trade-In
      if (orderForm.service_type === 'tradein' && distanceAB > 0) {
        pricingUrl += `&distance_ab=${distanceAB.toFixed(2)}`;
      }
      
      const response = await fetch(pricingUrl, { 
        headers: { 'Authorization': 'Bearer ' + token } 
      });

      if (response.ok) {
        // Clone response to prevent "body already used" errors
        const responseClone = response.clone();
        let data;
        try {
          data = await response.json();
        } catch (e) {
          data = await responseClone.json();
        }
        setPricing({
          delivery_price: data.delivery_price,
          bc_service_price: data.bc_service_price,
          tradein_price: data.tradein_price
        });

        const basePrice = orderForm.service_type === 'delivery' ? data.delivery_price :
                         orderForm.service_type === 'bc_service' ? data.bc_service_price :
                         data.tradein_price;
        
        setOrderForm(prev => ({ ...prev, base_price: basePrice }));
        
        // Show breakdown if available
        if (data.breakdown && data.breakdown[orderForm.service_type]) {
          const breakdown = data.breakdown[orderForm.service_type];
          let message = `Distancia total: ${distanceInKm} km`;
          if (breakdown.leg_ab && breakdown.leg_bc) {
            message += `\nA→B: ${distanceAB.toFixed(0)} km (€${breakdown.leg_ab.toFixed(2)}) | B→C: ${distanceBC.toFixed(0)} km (€${breakdown.leg_bc.toFixed(2)}, ${breakdown.discount_bc} descuento)`;
          } else if (breakdown.leg_ab && breakdown.leg_ba) {
            message += `\nA→B: ${distanceAB.toFixed(0)} km (€${breakdown.leg_ab.toFixed(2)}) | B→A: ${distanceAB.toFixed(0)} km (€${breakdown.leg_ba.toFixed(2)}, ${breakdown.discount_ba} descuento)`;
          }
          toast.success(message);
        } else {
          toast.success(`Distancia calculada: ${distanceInKm} km - Precio: €${basePrice.toFixed(2)}`);
        }
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      toast.error(t('error_occurred'));
    } finally {
      setCalculatingDistance(false);
    }
  };

  useEffect(() => {
    if (orderForm.pickup_lat && orderForm.delivery_lat) {
      // For B-C service, also need point C before calculating
      if (orderForm.service_type === 'bc_service') {
        if (orderForm.point_c_lat && orderForm.point_c_lng) {
          calculateDistance();
        }
      } else {
        calculateDistance();
      }
    }
  }, [orderForm.pickup_lat, orderForm.delivery_lat, orderForm.point_c_lat, orderForm.point_c_lng, orderForm.service_type]);

  // Load invoices when switching to invoices tab
  useEffect(() => {
    if (activeTab === 'invoices') {
      loadInvoices();
    }
  }, [activeTab]);

  const handleCreateOrder = async (e) => {
    e.preventDefault();

    if (orderForm.service_type === 'representacion') {
      if (!orderForm.pickup_lat) {
        toast.error(t('address_required'));
        return;
      }
      if (!orderForm.pickup_date_start || !orderForm.pickup_date_end) {
        toast.error(t('client_pickup_date_required'));
        return;
      }
      if (!String(orderForm.buyer_phone || '').trim() || !String(orderForm.seller_phone || '').trim()) {
        toast.error(t('client_repr_phones_required'));
        return;
      }
    } else if (!orderForm.pickup_lat || !orderForm.delivery_lat) {
      toast.error(t('address_required'));
      return;
    }

    if (!orderForm.pickup_date_start || !orderForm.pickup_date_end) {
      toast.error(t('client_pickup_date_required'));
      return;
    }

    // Validate second vehicle for BC and Trade-in services
    if (orderForm.service_type === 'bc_service') {
      // Validate second vehicle data
      if (!orderForm.second_car_plate || !orderForm.second_car_brand) {
        toast.error(t('client_validation_bc_second_vehicle'));
        return;
      }
      // Validate Point C (delivery address for second vehicle)
      if (!orderForm.point_c_lat || !orderForm.point_c_lng) {
        toast.error(t('client_validation_bc_point_c'));
        return;
      }
      // Validate Point C contact info
      if (!orderForm.point_c_contact_name || !orderForm.point_c_contact_phone) {
        toast.error(t('client_validation_bc_point_c_contact'));
        return;
      }
    } else if (orderForm.service_type === 'tradein') {
      // Trade-in only needs second vehicle data
      if (!orderForm.second_car_plate || !orderForm.second_car_brand) {
        toast.error(t('client_validation_tradein_second_vehicle'));
        return;
      }
    }

    // Si no hay distancia calculada, calcularla primero
    if (!orderForm.distance_km || orderForm.distance_km === 0) {
      console.log('⚠️ Distance not calculated, calculating now...');
      try {
        await calculateDistance();
        // Wait a moment for the distance to be set
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Error calculating distance:', error);
        toast.error(t('client_toast_error_calculate_distance'));
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      
      const pickup_time_range = orderForm.pickup_time_start && orderForm.pickup_time_end
        ? orderForm.pickup_time_start + ' - ' + orderForm.pickup_time_end
        : '';

      // Representación: same verification window for pickup and delivery (same address).
      // The backend parses *both* margin strings; if delivery has no dates, delivery_date_*
      // stays empty and the admin UI falls back to created_at for "Entrega".
      const delivery_time_range =
        orderForm.service_type === 'representacion'
          ? pickup_time_range
          : orderForm.delivery_time_start && orderForm.delivery_time_end
            ? orderForm.delivery_time_start + ' - ' + orderForm.delivery_time_end
            : '';

      const fmtMarginApiDate = (d) => {
        if (!d) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const pickup_margin_time = orderForm.pickup_date_start && orderForm.pickup_date_end
        ? `${fmtMarginApiDate(orderForm.pickup_date_start)} - ${fmtMarginApiDate(orderForm.pickup_date_end)} | ${pickup_time_range}`
        : pickup_time_range;

      let deliveryDateStart = orderForm.delivery_date_start;
      let deliveryDateEnd = orderForm.delivery_date_end;
      if (orderForm.service_type === 'representacion') {
        deliveryDateStart = orderForm.pickup_date_start;
        deliveryDateEnd = orderForm.pickup_date_end;
      } else if (
        orderForm.pickup_date_end &&
        (orderForm.delivery_time_start || orderForm.delivery_time_end) &&
        (!deliveryDateStart || !deliveryDateEnd)
      ) {
        deliveryDateStart = deliveryDateStart || orderForm.pickup_date_end;
        deliveryDateEnd = deliveryDateEnd || orderForm.pickup_date_end;
      }

      const delivery_margin_time = deliveryDateStart && deliveryDateEnd
        ? `${fmtMarginApiDate(deliveryDateStart)} - ${fmtMarginApiDate(deliveryDateEnd)} | ${delivery_time_range}`
        : delivery_time_range;

      const secondVehicle = orderForm.has_second_vehicle ? {
        car_type: orderForm.second_car_type,
        car_brand: orderForm.second_car_brand,
        car_model: orderForm.second_car_model,
        car_plate: orderForm.second_car_plate,
        fuel_type: orderForm.second_fuel_type
      } : null;

      const effectiveDeliveryAddress = orderForm.service_type === 'representacion'
        ? (orderForm.pickup_address || '')
        : orderForm.delivery_address;
      const effectiveDeliveryLat = orderForm.service_type === 'representacion'
        ? orderForm.pickup_lat
        : orderForm.delivery_lat;
      const effectiveDeliveryLng = orderForm.service_type === 'representacion'
        ? orderForm.pickup_lng
        : orderForm.delivery_lng;

      const buyerFullName = `${orderForm.buyer_name || ''} ${orderForm.buyer_surname || ''}`.trim();
      const sellerFullName = `${orderForm.seller_name || ''} ${orderForm.seller_surname || ''}`.trim();

      const payload = {
        service_type: orderForm.service_type,
        pickup_address: orderForm.pickup_address,
        pickup_lat: orderForm.pickup_lat,
        pickup_lng: orderForm.pickup_lng,
        pickup_contact_name:
          orderForm.service_type === 'representacion'
            ? buyerFullName || orderForm.pickup_contact_name
            : orderForm.pickup_contact_name,
        pickup_contact_phone:
          orderForm.service_type === 'representacion'
            ? String(orderForm.buyer_phone || '').trim() || orderForm.pickup_contact_phone
            : orderForm.pickup_contact_phone,
        pickup_margin_time: pickup_margin_time,
        delivery_address: effectiveDeliveryAddress,
        delivery_lat: effectiveDeliveryLat,
        delivery_lng: effectiveDeliveryLng,
        delivery_contact_name:
          orderForm.service_type === 'representacion'
            ? sellerFullName || orderForm.delivery_contact_name
            : orderForm.delivery_contact_name,
        delivery_contact_phone:
          orderForm.service_type === 'representacion'
            ? String(orderForm.seller_phone || '').trim() || orderForm.delivery_contact_phone
            : orderForm.delivery_contact_phone,
        delivery_margin_time: delivery_margin_time,
        car_type: orderForm.car_type,
        car_brand: orderForm.car_brand,
        car_model: orderForm.car_model,
        car_plate: orderForm.car_plate,
        fuel_type: orderForm.fuel_type,
        distance_km: orderForm.distance_km,
        second_vehicle: secondVehicle,
        additional_services: orderForm.selected_additional_services.map(serviceId => ({
          service_id: serviceId,
          quantity: 1
        })),
        notes: orderForm.notes
      };

      const url = editingOrderId 
        ? `${BACKEND_URL}/api/orders/${editingOrderId}`
        : `${BACKEND_URL}/api/orders`;
      
      const method = editingOrderId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      // Clone response to prevent "body already used" errors from monitoring scripts
      const responseClone = response.clone();
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        responseData = await responseClone.json();
      }

      if (!response.ok) {
        console.error('Server error:', responseData);
        throw new Error(responseData.detail || (editingOrderId ? 'Failed to update order' : 'Failed to create order'));
      }

      const createdOrder = responseData;
      const orderId = editingOrderId || createdOrder?.id;

      if (orderId && orderAttachmentFiles.length > 0) {
        try {
          await uploadOrderServiceDocuments(orderId, orderAttachmentFiles, BACKEND_URL, token, false);
          setOrderClientDocsRefresh((n) => n + 1);
        } catch (uploadErr) {
          console.error(uploadErr);
          toast.warning(t('service_docs_upload_warn'), {
            description: uploadErr?.message || t('service_docs_upload_warn_generic'),
          });
        }
      }

      toast.success(editingOrderId ? t('order_updated') : t('order_created'));
      setShowOrderDialog(false);
      resetForm();
      setEditingOrderId(null);
      
      // Redirect to services tab and scroll to top
      setActiveTab('services');
      window.scrollTo(0, 0);
      loadData();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.message || t('error_occurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setOrderForm({
      service_type: 'delivery',
      pickup_address: '', pickup_lat: null, pickup_lng: null,
      pickup_contact_name: '', pickup_contact_phone: '',
      pickup_date_start: null, pickup_date_end: null, 
      pickup_time_start: '', pickup_time_end: '',
      delivery_address: '', delivery_lat: null, delivery_lng: null,
      delivery_contact_name: '', delivery_contact_phone: '',
      delivery_date_start: null, delivery_date_end: null, 
      delivery_time_start: '', delivery_time_end: '',
      point_c_address: '', point_c_lat: null, point_c_lng: null,
      point_c_contact_name: '', point_c_contact_phone: '',
      point_c_date_start: null, point_c_date_end: null,
      point_c_time_start: '', point_c_time_end: '',
      car_type: '', car_brand: '', car_model: '', car_plate: '', fuel_type: '',
      has_second_vehicle: false,
      second_car_type: '', second_car_brand: '', second_car_model: '',
      second_car_plate: '', second_fuel_type: '',
      distance_km: 0, base_price: 0,
      selected_additional_services: [], notes: '',
      first_vehicle_additional_services: [],
      first_vehicle_notes: '',
      second_vehicle_additional_services: [],
      second_vehicle_notes: '',
      buyer_name: '',
      buyer_surname: '',
      buyer_phone: '',
      buyer_dni: '',
      seller_name: '',
      seller_surname: '',
      seller_phone: '',
      seller_dni: '',
    });
    setOrderAttachmentFiles([]);
    setPricing({ delivery_price: 0, bc_service_price: 0, tradein_price: 0 });
    setEditingOrderId(null);
  };

  const handleEditOrder = (order) => {
    console.log('Editing order:', order); // Debug log
    
    // Parse pickup_margin_time and delivery_margin_time to extract dates and times
    // Format: "dd/MM/yyyy - dd/MM/yyyy | HH:mm - HH:mm"
    const parseMarginTime = (marginTime) => {
      if (!marginTime) return { dateStart: null, dateEnd: null, timeStart: '', timeEnd: '' };
      
      try {
        // Example: "01/01/2025 - 02/01/2025 | 09:00 - 18:00"
        const parts = marginTime.split('|');
        
        if (parts.length >= 1) {
          const datePart = parts[0].trim();
          const timePart = parts.length > 1 ? parts[1].trim() : '';
          
          // Parse dates
          const dateMatch = datePart.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s*-\s*(\d{1,2}\/\d{1,2}\/\d{4})/);
          let dateStart = null;
          let dateEnd = null;
          
          if (dateMatch) {
            const [, startDateStr, endDateStr] = dateMatch;
            
            // Parse dates from dd/MM/yyyy to Date objects
            const [startDay, startMonth, startYear] = startDateStr.split('/');
            const [endDay, endMonth, endYear] = endDateStr.split('/');
            
            dateStart = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
            dateEnd = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
          }
          
          // Parse times
          let timeStart = '';
          let timeEnd = '';
          
          if (timePart) {
            const timeMatch = timePart.match(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
            if (timeMatch) {
              [, timeStart, timeEnd] = timeMatch;
            }
          }
          
          return { dateStart, dateEnd, timeStart, timeEnd };
        }
      } catch (error) {
        console.error('Error parsing margin time:', error, 'Input:', marginTime);
      }
      
      return { dateStart: null, dateEnd: null, timeStart: '', timeEnd: '' };
    };

    const pickupTimes = parseMarginTime(order.pickup_margin_time);
    const deliveryTimes = parseMarginTime(order.delivery_margin_time);

    let pickupDateStart = pickupTimes.dateStart;
    let pickupDateEnd = pickupTimes.dateEnd;
    if (!pickupDateStart && order.pickup_date_from) {
      try {
        pickupDateStart = new Date(order.pickup_date_from);
        pickupDateEnd = new Date(order.pickup_date_to || order.pickup_date_from);
      } catch (_) {
        /* ignore */
      }
    }

    let deliveryDateStart = deliveryTimes.dateStart;
    let deliveryDateEnd = deliveryTimes.dateEnd;
    if (order.service_type === 'representacion') {
      deliveryDateStart = pickupDateStart || deliveryDateStart;
      deliveryDateEnd = pickupDateEnd || deliveryDateEnd;
    } else if (!deliveryDateStart && order.delivery_date_from) {
      try {
        deliveryDateStart = new Date(order.delivery_date_from);
        deliveryDateEnd = new Date(order.delivery_date_to || order.delivery_date_from);
      } catch (_) {
        /* ignore */
      }
    }

    const splitContactName = (full) => {
      const s = (full || '').trim();
      if (!s) return { first: '', last: '' };
      const i = s.indexOf(' ');
      if (i === -1) return { first: s, last: '' };
      return { first: s.slice(0, i).trim(), last: s.slice(i + 1).trim() };
    };
    const buyerParts =
      order.service_type === 'representacion'
        ? splitContactName(order.pickup_contact_name)
        : { first: '', last: '' };
    const sellerParts =
      order.service_type === 'representacion'
        ? splitContactName(order.delivery_contact_name)
        : { first: '', last: '' };
    
    console.log('Pickup margin time:', order.pickup_margin_time);
    console.log('Parsed pickup times:', pickupTimes);
    console.log('Delivery margin time:', order.delivery_margin_time);
    console.log('Parsed delivery times:', deliveryTimes);
    
    // Load order data into form
    setOrderForm({
      service_type: order.service_type,
      pickup_address: order.pickup_address,
      pickup_lat: order.pickup_lat,
      pickup_lng: order.pickup_lng,
      pickup_contact_name: order.pickup_contact_name,
      pickup_contact_phone: order.pickup_contact_phone,
      pickup_date_start: pickupDateStart,
      pickup_date_end: pickupDateEnd,
      pickup_time_start: pickupTimes.timeStart || order.pickup_time_start || '',
      pickup_time_end: pickupTimes.timeEnd || order.pickup_time_end || '',
      delivery_address: order.delivery_address,
      delivery_lat: order.delivery_lat,
      delivery_lng: order.delivery_lng,
      delivery_contact_name: order.delivery_contact_name,
      delivery_contact_phone: order.delivery_contact_phone,
      delivery_date_start: deliveryDateStart,
      delivery_date_end: deliveryDateEnd,
      delivery_time_start:
        order.service_type === 'representacion'
          ? pickupTimes.timeStart || order.pickup_time_start || ''
          : deliveryTimes.timeStart || order.delivery_time_start || '',
      delivery_time_end:
        order.service_type === 'representacion'
          ? pickupTimes.timeEnd || order.pickup_time_end || ''
          : deliveryTimes.timeEnd || order.delivery_time_end || '',
      point_c_address: order.point_c_address || '',
      point_c_lat: order.point_c_lat || null,
      point_c_lng: order.point_c_lng || null,
      point_c_contact_name: order.point_c_contact_name || '',
      point_c_contact_phone: order.point_c_contact_phone || '',
      point_c_date_start: order.point_c_date_start ? new Date(order.point_c_date_start) : null,
      point_c_date_end: order.point_c_date_end ? new Date(order.point_c_date_end) : null,
      point_c_time_start: order.point_c_time_start || '',
      point_c_time_end: order.point_c_time_end || '',
      car_type: order.car_type,
      car_brand: order.car_brand,
      car_model: order.car_model,
      car_plate: order.car_plate,
      fuel_type: order.fuel_type,
      has_second_vehicle: order.has_second_vehicle || order.second_vehicle ? true : false,
      second_car_type: order.second_vehicle?.car_type || order.second_car_type || '',
      second_car_brand: order.second_vehicle?.car_brand || order.second_car_brand || '',
      second_car_model: order.second_vehicle?.car_model || order.second_car_model || '',
      second_car_plate: order.second_vehicle?.car_plate || order.second_car_plate || '',
      second_fuel_type: order.second_vehicle?.fuel_type || order.second_fuel_type || '',
      distance_km: order.distance_km,
      base_price: order.base_price,
      // Map additional_services array from backend to just IDs
      selected_additional_services: Array.isArray(order.additional_services) 
        ? order.additional_services.map(service => service.service_id) 
        : [],
      notes: order.notes || '',
      first_vehicle_additional_services: order.first_vehicle_additional_services || [],
      first_vehicle_notes: order.first_vehicle_notes || '',
      second_vehicle_additional_services: order.second_vehicle_additional_services || [],
      second_vehicle_notes: order.second_vehicle_notes || '',
      buyer_name: order.service_type === 'representacion' ? buyerParts.first : '',
      buyer_surname: order.service_type === 'representacion' ? buyerParts.last : '',
      buyer_phone: order.service_type === 'representacion' ? (order.pickup_contact_phone || '') : '',
      buyer_dni: '',
      seller_name: order.service_type === 'representacion' ? sellerParts.first : '',
      seller_surname: order.service_type === 'representacion' ? sellerParts.last : '',
      seller_phone: order.service_type === 'representacion' ? (order.delivery_contact_phone || '') : '',
      seller_dni: '',
    });
    setOrderAttachmentFiles([]);
    setShowOrderDialog(true);
    // Store the order ID for updating
    setEditingOrderId(order.id);
  };

  const handleCancelOrder = async (orderId) => {
    console.log('handleCancelOrder called with orderId:', orderId);
    setOrderToCancel(orderId);
    setShowCancelDialog(true);
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const confirmCancelOrder = async () => {
    if (!orderToCancel) return;
    
    console.log('confirmCancelOrder called with orderId:', orderToCancel);
    
    try {
      const token = localStorage.getItem('session_token');
      console.log('Token:', token ? 'exists' : 'missing');
      console.log('Calling backend URL:', `${BACKEND_URL}/api/orders/${orderToCancel}/cancel`);
      
      const response = await fetch(`${BACKEND_URL}/api/orders/${orderToCancel}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      
      // Some errors can return non-JSON bodies; read text first and JSON.parse if possible.
      const bodyText = await response.text();
      let data = null;
      try {
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch {
        data = null;
      }
      
      if (response.ok) {
        if (!data) {
          throw new Error('Unexpected server response');
        }
        console.log('Cancellation successful:', data);
        toast.success(`Servicio cancelado: ${data.status}. Cargo: €${data.cancellation_fee?.toFixed(2) || 0}`);
        setShowCancelDialog(false);
        setOrderToCancel(null);
        loadData();
      } else {
        console.error('Cancellation failed:', data);
        toast.error(data?.detail || 'Error al cancelar el servicio');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Error al cancelar el servicio');
    }
  };

  const getTotalPrice = () => {
    let total = orderForm.base_price || 0;
    
    // For B-C and Trade-In: sum services from both vehicles
    if (orderForm.service_type === 'bc_service' || orderForm.service_type === 'tradein') {
      (orderForm.first_vehicle_additional_services || []).forEach(serviceId => {
        const service = additionalServices.find(s => s.id === serviceId);
        if (service) total += (service.price || 0);
      });
      (orderForm.second_vehicle_additional_services || []).forEach(serviceId => {
        const service = additionalServices.find(s => s.id === serviceId);
        if (service) total += (service.price || 0);
      });
    } else {
      // For delivery: use regular selected_additional_services
      (orderForm.selected_additional_services || []).forEach(serviceId => {
        const service = additionalServices.find(s => s.id === serviceId);
        if (service) total += (service.price || 0);
      });
    }
    
    return total;
  };

  // Check if user is from Spain (to apply VAT)
  const isSpanishCompany = () => {
    return user?.country === 'ES';
  };

  // Calculate VAT (21% for Spanish companies)
  const getVAT = () => {
    if (!isSpanishCompany()) return 0;
    return getTotalPrice() * 0.21;
  };

  // Get total price with VAT (only for Spanish companies)
  const getTotalWithVAT = () => {
    const basePrice = getTotalPrice();
    if (isSpanishCompany()) {
      return basePrice + getVAT();
    }
    return basePrice;
  };

  // Extract unique cities from orders (from pickup and delivery addresses)
  const getCities = () => {
    const cities = new Set();
    orders.forEach(order => {
      if (order.pickup_address) {
        const pickupCity = order.pickup_address.split(',')[0].trim();
        cities.add(pickupCity);
      }
      if (order.delivery_address) {
        const deliveryCity = order.delivery_address.split(',')[0].trim();
        cities.add(deliveryCity);
      }
    });
    return Array.from(cities).sort();
  };

  // Filter orders based on all filter criteria
  const filteredOrders = orders.filter(order => {
    // Filter by sub-tab: active or historical
    if (servicesSubTab === 'active') {
      // En Proceso: servicios que NO están completados ni cancelados
      const isCancelled = order.status && (
        order.status.includes('Cancelado') || 
        order.status === 'cancelled'
      );
      if (order.status === 'delivered' || isCancelled) return false;
    } else if (servicesSubTab === 'historical') {
      // Histórico: servicios completados O cancelados (incluyendo Cancelado en Salida y Cancelado en Transporte aprobado)
      const isCancelled = order.status && (
        order.status.includes('Cancelado') || 
        order.status === 'cancelled'
      );
      // Note: "Cancelado en Transporte - Pendiente" stays in active until approved
      if (order.status !== 'delivered' && !isCancelled) return false;
    }

    // Filter by car plate
    if (filters.searchPlate && !order.car_plate?.toLowerCase().includes(filters.searchPlate.toLowerCase())) {
      return false;
    }
    
    // Filter by payment status
    if (filters.paymentStatus !== 'all' && order.payment_status !== filters.paymentStatus) {
      return false;
    }
    
    // Filter by order status
    if (filters.orderStatus !== 'all') {
      // Special handling for 'in_progress_filter' to include multiple statuses
      if (filters.orderStatus === 'in_progress_filter') {
        const inProgressStatuses = ['assigned', 'por_asignar', 'pending', 'confirmed', 'in_transit'];
        if (!inProgressStatuses.includes(order.status)) {
          return false;
        }
      } else if (order.status !== filters.orderStatus) {
        return false;
      }
    }
    
    // Filter by service type
    if (filters.serviceType !== 'all' && order.service_type !== filters.serviceType) {
      return false;
    }
    
    // Filter by city (check both pickup and delivery with partial match)
    if (filters.city !== 'all' && filters.city.trim() !== '') {
      const pickupAddress = order.pickup_address?.toLowerCase() || '';
      const deliveryAddress = order.delivery_address?.toLowerCase() || '';
      const filterCity = filters.city.toLowerCase().trim();
      if (!pickupAddress.includes(filterCity) && !deliveryAddress.includes(filterCity)) {
        return false;
      }
    }
    
    // Filter by date range
    if (filters.dateFrom) {
      const orderDate = new Date(order.created_at);
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (orderDate < fromDate) {
        return false;
      }
    }
    
    if (filters.dateTo) {
      const orderDate = new Date(order.created_at);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (orderDate > toDate) {
        return false;
      }
    }
    
    return true;
  });

  const clearFilters = () => {
    setFilters({
      searchPlate: '',
      paymentStatus: 'all',
      orderStatus: 'all',
      serviceType: 'all',
      city: 'all',
      dateFrom: null,
      dateTo: null
    });
  };

  // Calculate dashboard statistics
  const getStatistics = () => {
    const stats = {
      total: orders.length,
      in_progress: orders.filter(o => o.status === 'assigned' || o.status === 'por_asignar').length,
      completed: orders.filter(o => o.status === 'delivered').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      pending_payment: orders.filter(o => o.payment_status === 'pending').length,
      total_spent: orders.reduce((sum, o) => sum + (o.total_price || 0), 0),
      by_type: {
        delivery: orders.filter(o => o.service_type === 'delivery').length,
        bc_service: orders.filter(o => o.service_type === 'bc_service').length,
        tradein: orders.filter(o => o.service_type === 'tradein').length,
        representacion: orders.filter(o => o.service_type === 'representacion').length
      }
    };
    return stats;
  };

  const stats = getStatistics();

  // Prepare chart data
  const statusChartData = [
    { name: t('in_progress'), value: stats.in_progress, color: '#3b82f6' },
    { name: t('completed'), value: stats.completed, color: '#36e961' },
    { name: t('cancelled'), value: stats.cancelled, color: '#ef4444' }
  ].filter(item => item.value > 0);

  const serviceTypeChartData = [
    { name: t('delivery'), value: stats.by_type.delivery },
    { name: t('bc_service'), value: stats.by_type.bc_service },
    { name: t('tradein'), value: stats.by_type.tradein },
    { name: t('representacion'), value: stats.by_type.representacion }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Calculate stats for sidebar
  const sidebarStats = {
    total: orders.length,
    in_progress: orders.filter(o => 
      ['assigned', 'por_asignar', 'pending', 'confirmed', 'in_transit'].includes(o.status)
    ).length,
    completed: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled' || o.status?.includes('Cancelado')).length,
  };

  // Full-screen Command Center for Dashboard tab
  if (activeTab === 'dashboard') {
    return (
      <ClientDashboardHome 
        user={user} 
        orders={orders} 
        onCreateService={() => {
          setActiveTab('create');
          setShowOrderDialog(true);
        }}
        onNavigateToTab={(tab) => setActiveTab(tab)}
        onFilterServices={(filterType, serviceType) => {
          setActiveTab('services');
          if (filterType === 'all') {
            setFilters({
              searchPlate: '',
              paymentStatus: 'all',
              orderStatus: 'all',
              serviceType: 'all',
              city: 'all',
              dateFrom: null,
              dateTo: null
            });
          } else if (filterType === 'in_progress') {
            setFilters({
              searchPlate: '',
              paymentStatus: 'all',
              orderStatus: 'in_progress_filter',
              serviceType: 'all',
              city: 'all',
              dateFrom: null,
              dateTo: null
            });
          } else if (filterType === 'completed') {
            setFilters({
              searchPlate: '',
              paymentStatus: 'all',
              orderStatus: 'delivered',
              serviceType: 'all',
              city: 'all',
              dateFrom: null,
              dateTo: null
            });
          } else if (filterType === 'cancelled') {
            setFilters({
              searchPlate: '',
              paymentStatus: 'all',
              orderStatus: 'cancelled',
              serviceType: 'all',
              city: 'all',
              dateFrom: null,
              dateTo: null
            });
          }
        }}
        onSelectService={(orderId) => {
          setActiveTab('services');
          setFilters({
            searchPlate: '',
            paymentStatus: 'all',
            orderStatus: 'all',
            serviceType: 'all',
            city: 'all',
            dateFrom: null,
            dateTo: null
          });
          setSelectedOrderId(orderId);
          const orderDetails = orders.find(o => o.id === orderId);
          setSelectedOrder(orderDetails);
          setShowOrderDetails(true);
        }}
        onLogout={handleLogout}
      />
    );
  }

  // All other tabs use sidebar layout
  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <ClientSidebar
        user={user}
        activeTab={activeTab}
        stats={sidebarStats}
        onNavigate={(tab) => {
          setActiveTab(tab);
          if (tab === 'invoices' && invoices.length === 0) {
            loadInvoices();
          }
        }}
        onCreateService={() => {
          setActiveTab('create');
          setShowOrderDialog(true);
        }}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 lg:p-8">
        {activeTab === 'services' && (
          <>
        {/* Sub-tabs for Active / Historical + Create Button */}
        <div className="flex items-center justify-between mb-6 border-b bg-white rounded-t-lg px-6 pt-4">
          <div className="flex gap-2">
            <button
              onClick={() => setServicesSubTab('active')}
              className={`px-4 py-2 font-medium transition-colors relative ${
                servicesSubTab === 'active'
                  ? 'text-[#36e961] border-b-2 border-[#36e961]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('driver_services_in_progress')}
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                {orders.filter(o => {
                  const isCancelled = o.status && (o.status.includes('Cancelado') || o.status === 'cancelled');
                  return o.status !== 'delivered' && !isCancelled;
                }).length}
              </span>
            </button>
            <button
              onClick={() => setServicesSubTab('historical')}
              className={`px-4 py-2 font-medium transition-colors relative ${
                servicesSubTab === 'historical'
                  ? 'text-[#36e961] border-b-2 border-[#36e961]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('admin_historical')}
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                {orders.filter(o => {
                  const isCancelled = o.status && (o.status.includes('Cancelado') || o.status === 'cancelled');
                  return o.status === 'delivered' || isCancelled;
                }).length}
              </span>
            </button>
          </div>
          
          <Button 
            onClick={() => setShowOrderDialog(true)}
            className="text-white"
            style={{backgroundColor: '#36e961'}}
            data-testid="create-service-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('create_order')}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">{t('filter_orders')}</CardTitle>
            <CardDescription>{t('filter_description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {/* Car Plate Search */}
              <div className="space-y-2">
                <Label htmlFor="search-plate" className="text-sm font-medium">
                  {t('search_by_plate')}
                </Label>
                <Input
                  id="search-plate"
                  type="text"
                  placeholder={t('enter_plate_number')}
                  value={filters.searchPlate}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchPlate: e.target.value }))}
                  data-testid="search-plate-input"
                />
              </div>

              {/* Payment Status Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('payment_status')}</Label>
                <Select
                  value={filters.paymentStatus}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, paymentStatus: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all')}</SelectItem>
                    <SelectItem value="pending">{t('payment_status_pending')}</SelectItem>
                    <SelectItem value="paid">{t('paid')}</SelectItem>
                    <SelectItem value="failed">{t('failed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Order Status Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('order_status')}</Label>
                <Select
                  value={filters.orderStatus}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, orderStatus: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all')}</SelectItem>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="confirmed">{t('confirmed')}</SelectItem>
                    <SelectItem value="assigned">{t('assigned')}</SelectItem>
                    <SelectItem value="in_transit">{t('in_transit')}</SelectItem>
                    <SelectItem value="delivered">{t('delivered')}</SelectItem>
                    <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                    <SelectItem value="Cancelado en Garantía">{t('Cancelado en Garantía')}</SelectItem>
                    <SelectItem value="Cancelado con -24h antelación">{t('Cancelado con -24h antelación')}</SelectItem>
                    <SelectItem value="Cancelado con +24h de antelación">{t('Cancelado con +24h de antelación')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Service Type Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('service_type')}</Label>
                <Select
                  value={filters.serviceType}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, serviceType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all')}</SelectItem>
                    <SelectItem value="delivery">{t('delivery')}</SelectItem>
                    <SelectItem value="bc_service">{t('bc_service')}</SelectItem>
                    <SelectItem value="tradein">{t('tradein')}</SelectItem>
                    <SelectItem value="representacion">{t('representacion')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* City Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('filter_by_city')}</Label>
                <Input
                  type="text"
                  placeholder="Escribe una ciudad..."
                  value={filters.city === 'all' ? '' : filters.city}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value || 'all' }))}
                  className="w-full"
                />
              </div>

              {/* Date From */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('date_from')}</Label>
                <DatePicker {...DATE_PICKER_POPPER_PROPS}
                  selected={filters.dateFrom}
                  onChange={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={t('select_date')}
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                />
              </div>

              {/* Date To */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('date_to')}</Label>
                <DatePicker {...DATE_PICKER_POPPER_PROPS}
                  selected={filters.dateTo}
                  onChange={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={t('select_date')}
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                {t('clear_filters')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {filteredOrders.length === 0 ? (
          <Card className="text-center py-12">
            <CardHeader>
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <CardTitle>
                {orders.length === 0 ? t('no_orders_yet') : t('no_orders_found')}
              </CardTitle>
              <CardDescription>
                {orders.length === 0 ? t('create_first_order') : t('try_different_filters')}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {filteredOrders.map((order) => {
              const isExpanded = expandedOrders.has(order.id);
              return (
              <Card key={order.id} className="card-hover" data-order-id={order.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <span>#{order.id.substring(0, 8).toUpperCase()}</span>
                          <span className="text-sm font-medium px-2 py-1 rounded" style={{backgroundColor: '#36e961', color: 'white'}}>
                            {t(order.service_type)}
                          </span>
                        </CardTitle>
                        {/* Driver info if assigned */}
                        {order.driver_name && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">👤 Conductor:</span>
                            <span className="font-semibold text-gray-900">{order.driver_name}</span>
                            {order.driver_phone && (
                              <span className="text-gray-600">• 📞 {order.driver_phone}</span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Vehicle info preview */}
                      <div className="mt-1 flex items-center gap-2 text-sm text-gray-700">
                        <Car className="w-4 h-4" style={{color: '#36e961'}} />
                        <span className="font-semibold">{order.car_plate}</span>
                        <span>•</span>
                        <span>{order.car_brand} {order.car_model}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <span style={{color: '#000000'}}>{getFuelIcon(order.fuel_type)}</span>
                          <span>{t(order.fuel_type)}</span>
                        </span>
                      </div>
                      
                      {/* Service Dates and Times - Pickup and Delivery */}
                      {(order.pickup_margin_time ||
                        order.delivery_margin_time ||
                        order.pickup_date_from ||
                        order.pickup_date_to ||
                        order.delivery_date_from ||
                        order.delivery_date_to ||
                        order.pickup_time_start ||
                        order.pickup_time_end ||
                        order.delivery_time_start ||
                        order.delivery_time_end) && (
                        <div className="mt-3 space-y-2 text-sm">
                          {(order.pickup_margin_time ||
                            order.pickup_date_from ||
                            order.pickup_date_to ||
                            order.pickup_time_start ||
                            order.pickup_time_end) && (() => {
                            const pickupDisplay = resolveMarginDateTimeDisplay(
                              order.pickup_margin_time,
                              order.pickup_date_from,
                              order.pickup_date_to,
                              order.pickup_time_start,
                              order.pickup_time_end,
                              dateLocale,
                              t,
                              null
                            );
                            return (
                              <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">📤 Fecha Recogida:</span>
                                  <span className="font-semibold text-gray-900">{pickupDisplay.date}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">🕐 Hora Recogida:</span>
                                  <span className="font-semibold text-gray-900">{pickupDisplay.time}</span>
                                </div>
                              </div>
                            );
                          })()}
                          {(order.delivery_margin_time ||
                            order.delivery_date_from ||
                            order.delivery_date_to ||
                            order.delivery_time_start ||
                            order.delivery_time_end) && (() => {
                            const deliveryDisplay = resolveMarginDateTimeDisplay(
                              order.delivery_margin_time,
                              order.delivery_date_from,
                              order.delivery_date_to,
                              order.delivery_time_start,
                              order.delivery_time_end,
                              dateLocale,
                              t,
                              order.pickup_date_to || order.pickup_date_from
                            );
                            return (
                              <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">📥 Fecha Entrega:</span>
                                  <span className="font-semibold text-gray-900">{deliveryDisplay.date}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">🕐 Hora Entrega:</span>
                                  <span className="font-semibold text-gray-900">{deliveryDisplay.time}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Client Name and Addresses - Horizontal Layout */}
                      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-4 text-sm">
                        {/* Client */}
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">👤 Cliente:</span>
                          <span className="font-semibold text-gray-900">{order.client_name}</span>
                        </div>
                        
                        {/* Pickup Address */}
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#36e961'}} />
                          <div className="flex-1">
                            <div>
                              <span className="text-gray-600 font-medium">Recogida:</span>{' '}
                              <span className="text-gray-900">{order.pickup_address}</span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              <span className="font-medium">{order.pickup_contact_name}</span>
                              {order.pickup_contact_phone && (
                                <span> • {order.pickup_contact_phone}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Delivery Address */}
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#0a3042'}} />
                          <div className="flex-1">
                            <div>
                              <span className="text-gray-600 font-medium">{t('client_delivery_label')}</span>{' '}
                              <span className="text-gray-900">{order.delivery_address}</span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              <span className="font-medium">{order.delivery_contact_name}</span>
                              {order.delivery_contact_phone && (
                                <span> • {order.delivery_contact_phone}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <CardDescription className="mt-3">
                        <span className="text-gray-600">📝 {t('client_order_created_at_label')}</span>{' '}
                        {new Date(order.created_at).toLocaleDateString(dateLocale, {
                          year: 'numeric', month: 'long', day: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </CardDescription>
                    </div>
                    <div className="text-right space-y-2">
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-gray-600 font-medium">{t('order_status')}:</span>
                        <div className="flex items-center gap-2">
                          <span className={'status-badge status-' + clientVisibleOrderStatus(order)}>
                            {t(clientVisibleOrderStatus(order))}
                          </span>
                          {order.has_incident && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleIncidentExpansion(order.id);
                              }}
                              className={`bg-red-100 text-red-600 text-[10px] px-2 py-1 rounded-full flex items-center gap-1 hover:bg-red-200 transition-colors cursor-pointer border ${expandedIncidents.has(order.id) ? 'border-red-400 ring-2 ring-red-200' : 'border-transparent'}`}
                              title={t('client_incident_title_hint')}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              <span>{t('client_incident_badge')}</span>
                              {expandedIncidents.has(order.id) ? (
                                <ChevronUp className="w-3 h-3 ml-1" />
                              ) : (
                                <ChevronDown className="w-3 h-3 ml-1" />
                              )}
                            </button>
                          )}
                        </div>
                        
                        {/* Incident Details Expanded Section */}
                        {order.has_incident && expandedIncidents.has(order.id) && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                            {loadingIncident === order.id ? (
                              <div className="flex items-center gap-2 text-red-600">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">{t('client_incident_loading_details')}</span>
                              </div>
                            ) : incidentDetails[order.id]?.has_incident ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="w-5 h-5 text-red-600" />
                                  <span className="font-semibold text-red-800">
                                    {incidentDetails[order.id].incident_summary?.type || t('client_incident_type_fallback')}
                                  </span>
                                </div>
                                {incidentDetails[order.id].incident_summary?.description && (
                                  <p className="text-sm text-red-700 ml-7">
                                    {incidentDetails[order.id].incident_summary.description}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-4 ml-7 text-xs text-red-600">
                                  {incidentDetails[order.id].incident_summary?.reported_at && (
                                    <span>
                                      <strong>{t('client_incident_reported_label')}</strong>{' '}
                                      {new Date(incidentDetails[order.id].incident_summary.reported_at).toLocaleString(dateLocale)}
                                    </span>
                                  )}
                                  {incidentDetails[order.id].incident_summary?.resolved_at && (
                                    <span>
                                      <strong>{t('client_incident_resolved_label')}</strong>{' '}
                                      {new Date(incidentDetails[order.id].incident_summary.resolved_at).toLocaleString(dateLocale)}
                                    </span>
                                  )}
                                </div>
                                {incidentDetails[order.id].incident_summary?.resolution && (
                                  <div className="ml-7 mt-2 text-sm text-red-700">
                                    <strong>{t('client_incident_resolution_label')}</strong>{' '}
                                    {incidentDetails[order.id].incident_summary.resolution === 'service_cancelled_at_pickup' 
                                      ? t('client_incident_resolution_cancelled_at_pickup')
                                      : incidentDetails[order.id].incident_summary.resolution === 'service_cancelled'
                                      ? t('client_incident_resolution_cancelled')
                                      : incidentDetails[order.id].incident_summary.resolution === 'service_delivered'
                                      ? t('client_incident_resolution_delivered')
                                      : incidentDetails[order.id].incident_summary.resolution}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-red-600">
                                <AlertTriangle className="w-4 h-4 inline mr-2" />
                                {incidentDetails[order.id]?.message || t('client_incident_generic_notice')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-gray-600 font-medium">{t('payment_status')}:</span>
                        <span className={'status-badge status-' + order.payment_status}>
                          {translateClientPaymentStatus(order.payment_status, t)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Vista resumida - siempre visible */}
                  <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-4">
                      <div>
                        {isSpanishCompany() ? (
                          <>
                            <p className="text-sm text-gray-600">Subtotal (sin IVA)</p>
                            <p className="text-lg font-semibold text-gray-900">€{(order.total_price || order.price || 0).toFixed(2)}</p>
                            <p className="text-xs text-gray-500">+ IVA (21%): €{((order.total_price || order.price || 0) * 0.21).toFixed(2)}</p>
                            <p className="text-sm text-gray-600 mt-1">Total (con IVA)</p>
                            <p className="text-xl font-bold" style={{color: '#36e961'}}>€{((order.total_price || order.price || 0) * 1.21).toFixed(2)}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-600">{t('total_price')}</p>
                            <p className="text-xl font-bold" style={{color: '#36e961'}}>€{(order.total_price || order.price || 0).toFixed(2)}</p>
                            <p className="text-xs text-gray-500">(sin IVA)</p>
                          </>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">{t('calculated_distance')}</p>
                        <p className="text-base font-semibold text-gray-900">{order.distance_km} km</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Additional Services */}
                      {order.additional_services && order.additional_services.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          {order.additional_services.map((service, index) => {
                            const serviceName = typeof service === 'object' ? service.name : service;
                            return (
                              <span key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200">
                                <span className="text-base">{getServiceEmoji(serviceName)}</span>
                                <span className="text-gray-700 font-medium">{translateAdditionalServiceName(serviceName, t)}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Notes Preview in Summary */}
                      {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes) && !isExpanded && (
                        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm">
                          <span className="font-medium text-amber-900">📝 Tiene notas</span>
                        </div>
                      )}
                      
                      {/* Ver más button */}
                      <Button
                        onClick={() => toggleOrderExpansion(order.id)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            {t('show_less')}
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            {t('show_more')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Detalles expandidos - solo visible cuando isExpanded es true */}
                  {isExpanded && (
                    <>
                      <div className="grid md:grid-cols-3 gap-6 mt-6">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <MapPin className="w-4 h-4" style={{color: '#36e961'}} />
                            {t('pickup_information')}
                          </p>
                          <p className="text-sm text-gray-700">{order.pickup_address}</p>
                          <p className="text-xs text-gray-600 mt-1">{order.pickup_contact_name} • {order.pickup_contact_phone}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <MapPin className="w-4 h-4" style={{color: '#0a3042'}} />
                            {t('delivery_information')}
                          </p>
                          <p className="text-sm text-gray-700">{order.delivery_address}</p>
                          <p className="text-xs text-gray-600 mt-1">{order.delivery_contact_name} • {order.delivery_contact_phone}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Car className="w-4 h-4" style={{color: '#36e961'}} />
                            {t('vehicle_information')}
                          </p>
                          <p className="text-sm text-gray-700">{order.car_brand} {order.car_model}</p>
                          <p className="text-xs text-gray-600 mt-1">{order.car_plate}</p>
                          <p className="text-xs">
                            <span className="fuel-type-badge">{t(order.fuel_type)}</span>
                          </p>
                        </div>
                      </div>

                      {/* Driver Information - Only show if driver is assigned */}
                      {order.driver_name && (
                        <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                          <p className="text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                            <span className="text-black">👤</span>
                            {t('driver_info')}
                          </p>
                          <div className="grid md:grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-indigo-700 font-medium">{t('driver_name')}</p>
                              <p className="text-sm text-indigo-900 font-semibold">{order.driver_name}</p>
                            </div>
                            {order.driver_phone && (
                              <div>
                                <p className="text-xs text-indigo-700 font-medium flex items-center gap-1">
                                  <span className="text-black">📞</span>
                                  {t('driver_phone')}
                                </p>
                                <p className="text-sm text-indigo-900">{order.driver_phone}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Notes Section */}
                      {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes) && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                            📝 Notas del Servicio
                          </p>
                          {order.notes && (
                            <div className="mb-2">
                              <p className="text-xs text-amber-700 font-medium">Notas generales:</p>
                              <p className="text-sm text-amber-900">{order.notes}</p>
                            </div>
                          )}
                          {order.first_vehicle_notes && (
                            <div className="mb-2">
                              <p className="text-xs text-amber-700 font-medium">Notas vehículo 1:</p>
                              <p className="text-sm text-amber-900">{order.first_vehicle_notes}</p>
                            </div>
                          )}
                          {order.second_vehicle_notes && (
                            <div>
                              <p className="text-xs text-amber-700 font-medium">Notas vehículo 2:</p>
                              <p className="text-sm text-amber-900">{order.second_vehicle_notes}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {isExpanded && (
                        <ClientOrderDocumentsList
                          orderId={order.id}
                          backendUrl={BACKEND_URL}
                          refreshTrigger={orderClientDocsRefresh}
                          tokenStorageKey="session_token"
                          titleKey="client_order_docs_title"
                          emptyTextKey="client_order_doc_none"
                          compact
                        />
                      )}

                      {/* Botones de acción - solo visibles cuando está expandido */}
                      <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-end gap-2 flex-wrap">
                        {/* Descargar contrato (habilitado solo cuando el servicio tiene conductor asignado) */}
                        <Button
                          onClick={() => handleDownloadContract(order.id)}
                          variant="outline"
                          size="sm"
                          disabled={!order.driver_id || downloadingContractOrderId != null}
                          title={!order.driver_id ? t('service_detail_assign_driver_for_contract') : ''}
                          className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ borderColor: '#36e961', color: '#0a3042' }}
                        >
                          <FileText className="w-4 h-4" />
                          {t('service_detail_download_transport_contract')}
                        </Button>
                        
                        {/* "Ver Historial" only after delivery; "Ver Fotos / Ver Gastos" available
                            as soon as the driver has uploaded any peritaje/expense, regardless of
                            whether the order has been formally marked "delivered" yet. */}
                        {order.status === 'delivered' && (
                          <Button
                            onClick={() => {
                              setSelectedOrderForView(order);
                              setShowCompletionDetailsDialog(true);
                              loadCompletionDetails(order.id);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 border-green-500 text-green-700 hover:bg-green-50"
                          >
                            <FileText className="w-4 h-4" />
                            Ver Historial
                          </Button>
                        )}
                        {(
                          order.status === 'delivered' ||
                          order.has_pickup_photos ||
                          order.has_delivery_photos ||
                          order.has_pickup_inspection ||
                          order.has_delivery_inspection ||
                          order.pickup_inspection_id ||
                          order.delivery_inspection_id ||
                          order.inspection_id
                        ) && (
                          <Button
                            onClick={() => {
                              setSelectedOrderForView(order);
                              setShowPhotosDialog(true);
                              loadVehiclePhotos(order.id);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <Camera className="w-4 h-4" />
                            Ver Fotos del Vehículo
                          </Button>
                        )}
                        {(
                          order.status === 'delivered' ||
                          order.has_expenses ||
                          order.expense_report_status ||
                          order.pending_expenses_count > 0
                        ) && (
                          <Button
                            onClick={() => {
                              setSelectedOrderForView(order);
                              setShowExpensesDialog(true);
                              loadOrderExpenses(order.id);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <DollarSign className="w-4 h-4" />
                            Ver Gastos
                          </Button>
                        )}
                        
                        {!order.status?.includes('Cancelado') && order.status !== 'cancelled' && order.status !== 'delivered' && (
                          <>
                            <Button
                              onClick={() => handleEditOrder(order)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Edit className="w-4 h-4" />
                              {t('edit_order')}
                            </Button>
                            <Button
                              onClick={() => handleCancelOrder(order.id)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('cancel_order')}
                            </Button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )})}
          </div>
        )}
          </>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <CompanyProfile 
            user={user} 
            onUpdateProfile={(updatedUser) => {
              setUser(updatedUser);
            }}
          />
        )}

        {/* Deliveries Tab - Service Status */}
        {activeTab === 'deliveries' && (
          <ClientDeliveriesTab clientId={user?.id} />
        )}

        {/* GPS Live Tab - Real-time Tracking */}
        {activeTab === 'gps' && (
          <ClientGPSLiveTab />
        )}

        {/* Inspections Tab - AI Peritajes */}
        {activeTab === 'inspections' && (
          <ClientInspectionsTab clientId={user?.id} />
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Mis Facturas</h2>
                <p className="text-gray-600 mt-1">
                  {user?.payment_type === 'biweekly' 
                    ? 'Facturas quincenales generadas automáticamente' 
                    : 'Pagos instantáneos realizados'}
                </p>
              </div>
              <Button onClick={loadInvoices} disabled={loadingInvoices} variant="outline">
                {loadingInvoices ? 'Cargando...' : 'Actualizar'}
              </Button>
            </div>

            {loadingInvoices ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#36e961]" />
                <p className="mt-4 text-gray-500">Cargando facturas...</p>
              </div>
            ) : invoices.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium text-gray-900">No hay facturas disponibles</p>
                  <p className="text-sm text-gray-600 mt-2">
                    {user?.payment_type === 'biweekly' 
                      ? 'Las facturas se generan automáticamente cada 15 días' 
                      : 'Los pagos se procesan al momento de crear servicios'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <Card key={invoice.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-semibold">
                              {invoice.invoice_number || t('client_invoice_date_fallback', { date: new Date(invoice.invoice_date).toLocaleDateString(dateLocale) })}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                              invoice.status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {invoice.status === 'paid' ? t('paid') :
                               invoice.status === 'invoiced' ? t('client_invoice_status_invoiced') : t('payment_status_pending')}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Periodo:</span>
                              <p className="font-medium">
                                {new Date(invoice.period_start).toLocaleDateString(dateLocale)} - 
                                {new Date(invoice.period_end).toLocaleDateString(dateLocale)}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Fecha Factura:</span>
                              <p className="font-medium">{new Date(invoice.invoice_date).toLocaleDateString(dateLocale)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Servicios:</span>
                              <p className="font-medium">{invoice.service_ids?.length || 0}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Total:</span>
                              <p className="font-medium text-lg text-[#36e961]">{invoice.total_with_iva?.toFixed(2)}€</p>
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-3 gap-2 text-sm bg-gray-50 p-3 rounded">
                            <div>
                              <span className="text-gray-500">Base Imponible:</span>
                              <p className="font-medium">{(invoice.total_without_iva || invoice.total_amount)?.toFixed(2)}€</p>
                            </div>
                            <div>
                              <span className="text-gray-500">IVA (21%):</span>
                              <p className="font-medium">{(invoice.total_iva || invoice.iva_amount)?.toFixed(2)}€</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Total con IVA:</span>
                              <p className="font-medium text-[#36e961]">{invoice.total_with_iva?.toFixed(2)}€</p>
                            </div>
                          </div>
                        </div>

                        <div className="ml-4">
                          {invoice.status === 'invoiced' && invoice.invoice_pdf_url && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open(`${BACKEND_URL}${invoice.invoice_pdf_url}`, '_blank')}
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Ver PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        </div>
      </main>


      {/* Order Creation/Edit Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={(open) => {
            setShowOrderDialog(open);
            if (!open) {
              resetForm();
              // Redirect to services tab when closing the dialog
              if (activeTab === 'create') {
                setActiveTab('services');
              }
            }
          }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('create_new_order')}</DialogTitle>
                <DialogDescription>{t('fill_details')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateOrder} className="space-y-6" data-testid="create-order-form">
                
                <div className="space-y-4">
                  <Label>{t('service_type_required')}</Label>
                  <div className="grid grid-cols-4 gap-4">
                    {['delivery', 'bc_service', 'tradein', 'representacion'].map(type => (
                      <div
                        key={type}
                        onClick={() => {
                          // Set fixed price for Representación
                          if (type === 'representacion') {
                            setOrderForm(prev => ({
                              ...prev,
                              service_type: type,
                              base_price: 45.00,
                              distance_km: 0,
                              has_second_vehicle: false,
                              // Keep representation as single-location service.
                              delivery_address: prev.pickup_address || prev.delivery_address,
                              delivery_lat: prev.pickup_lat || prev.delivery_lat,
                              delivery_lng: prev.pickup_lng || prev.delivery_lng
                            }));
                          } else if (type === 'bc_service' || type === 'tradein') {
                            // BC and Trade-in services always require a second vehicle
                            setOrderForm(prev => ({ ...prev, service_type: type, has_second_vehicle: true }));
                          } else {
                            setOrderForm(prev => ({ ...prev, service_type: type, has_second_vehicle: false }));
                          }
                        }}
                        className={'p-6 border-2 rounded-lg cursor-pointer transition ' + (
                          orderForm.service_type === type ? 'border-[#36e961] bg-[#e8f9ed]' : 'border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <h4 className="font-semibold text-gray-900 mb-3 text-base">{t(type)}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{t(type + '_desc')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dirección - Only for Representación (simplified) */}
                {orderForm.service_type === 'representacion' && (
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg border-2 border-purple-300">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5" style={{color: '#8b5cf6'}} />
                    Dirección
                  </h3>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label>Dirección *</Label>
                      <AddressAutocomplete
                        value={orderForm.pickup_address}
                        onChange={(data) => setOrderForm(prev => ({
                          ...prev,
                          pickup_address: data.address || prev.pickup_address,
                          pickup_lat: data.lat || prev.pickup_lat,
                          pickup_lng: data.lng || prev.pickup_lng,
                          // For representation, destination is same as origin.
                          delivery_address: data.address || prev.delivery_address,
                          delivery_lat: data.lat || prev.delivery_lat,
                          delivery_lng: data.lng || prev.delivery_lng
                        }))}
                        placeholder="Dirección donde se encuentra el vehículo"
                        dataTestId="representacion-address-input"
                      />
                    </div>

                    {/* Toggle for Fixed vs Range Date/Time */}
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          {useFixedDateTime ? '📅 Fecha y Hora Fija' : '📅 Rango de Fecha y Hora'}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUseFixedDateTime(!useFixedDateTime)}
                        className="text-xs"
                        style={{borderColor: '#8b5cf6', color: '#8b5cf6'}}
                      >
                        {useFixedDateTime ? 'Cambiar a Rango' : 'Cambiar a Fecha Fija'}
                      </Button>
                    </div>

                    {/* Fixed Date/Time Mode */}
                    {useFixedDateTime ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fecha de Verificación *</Label>
                          <DatePicker {...DATE_PICKER_POPPER_PROPS}
                            selected={orderForm.pickup_date_start}
                            onChange={(date) => {
                              setOrderForm(prev => ({ 
                                ...prev, 
                                pickup_date_start: date,
                                pickup_date_end: date // Set same date for both
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholderText="Selecciona fecha"
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Hora de Verificación *</Label>
                          <Input
                            type="time"
                            value={orderForm.pickup_time_start}
                            onChange={(e) => setOrderForm(prev => ({ 
                              ...prev, 
                              pickup_time_start: e.target.value,
                              pickup_time_end: e.target.value // Set same time for both
                            }))}
                            required
                          />
                        </div>
                      </div>
                    ) : (
                      /* Range Date/Time Mode */
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fecha de Verificación *</Label>
                          <DatePicker {...DATE_PICKER_POPPER_PROPS}
                            selectsRange
                            startDate={orderForm.pickup_date_start}
                            endDate={orderForm.pickup_date_end}
                            onChange={(dates) => {
                              const [start, end] = dates;
                              setOrderForm(prev => ({ ...prev, pickup_date_start: start, pickup_date_end: end }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholderText={t('select_dates')}
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Hora de Verificación - Desde *</Label>
                          <Input
                            type="time"
                            value={orderForm.pickup_time_start}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, pickup_time_start: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Hora de Verificación - Hasta *</Label>
                          <Input
                            type="time"
                            value={orderForm.pickup_time_end}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, pickup_time_end: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Pickup Information - Not for Representación */}
                {orderForm.service_type !== 'representacion' && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5" style={{color: '#36e961'}} />
                    {t('pickup_information')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>{t('pickup_address')}</Label>
                      <AddressAutocomplete
                        value={orderForm.pickup_address}
                        onChange={(data) => setOrderForm(prev => ({
                          ...prev,
                          pickup_address: data.address || prev.pickup_address,
                          pickup_lat: data.lat || prev.pickup_lat,
                          pickup_lng: data.lng || prev.pickup_lng
                        }))}
                        placeholder={t('start_typing_address')}
                        dataTestId="pickup-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('contact_name')}</Label>
                      <Input
                        value={orderForm.pickup_contact_name}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, pickup_contact_name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('contact_phone')}</Label>
                      <Input
                        type="tel"
                        value={orderForm.pickup_contact_phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9+\s()-]/g, '');
                          setOrderForm(prev => ({ ...prev, pickup_contact_phone: value }));
                        }}
                        placeholder="+34 600 123 456"
                        pattern="[0-9+\s()-]*"
                        required
                      />
                    </div>

                    {/* Toggle for Fixed vs Range Date/Time - Pickup */}
                    <div className="md:col-span-2 flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          {useFixedPickupDateTime ? '📅 Fecha y Hora Fija' : '📅 Rango de Fecha y Hora'}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUseFixedPickupDateTime(!useFixedPickupDateTime)}
                        className="text-xs"
                        style={{borderColor: '#36e961', color: '#36e961'}}
                      >
                        {useFixedPickupDateTime ? 'Cambiar a Rango' : 'Cambiar a Fecha Fija'}
                      </Button>
                    </div>

                    {/* Fixed Date/Time Mode - Pickup */}
                    {useFixedPickupDateTime ? (
                      <>
                        <div className="space-y-2">
                          <Label>{t('pickup_date_range')}</Label>
                          <DatePicker {...DATE_PICKER_POPPER_PROPS}
                            selected={orderForm.pickup_date_start}
                            onChange={(date) => {
                              setOrderForm(prev => ({ 
                                ...prev, 
                                pickup_date_start: date,
                                pickup_date_end: date
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholderText="Selecciona fecha"
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('pickup_time')}</Label>
                          <Input
                            type="time"
                            value={orderForm.pickup_time_start}
                            onChange={(e) => setOrderForm(prev => ({ 
                              ...prev, 
                              pickup_time_start: e.target.value,
                              pickup_time_end: e.target.value
                            }))}
                            required
                          />
                        </div>
                      </>
                    ) : (
                      /* Range Date/Time Mode - Pickup */
                      <>
                        <div className="space-y-2">
                          <Label>{t('pickup_date_range')}</Label>
                          <DatePicker {...DATE_PICKER_POPPER_PROPS}
                            selectsRange
                            startDate={orderForm.pickup_date_start}
                            endDate={orderForm.pickup_date_end}
                            onChange={(dates) => {
                              const [start, end] = dates;
                              setOrderForm(prev => ({ ...prev, pickup_date_start: start, pickup_date_end: end }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholderText={t('select_dates')}
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('pickup_time')} - Desde</Label>
                          <Input
                            type="time"
                            value={orderForm.pickup_time_start}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, pickup_time_start: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('pickup_time')} - Hasta</Label>
                          <Input
                            type="time"
                            value={orderForm.pickup_time_end}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, pickup_time_end: e.target.value }))}
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                )}

                {/* Delivery Information - Not for Representación */}
                {orderForm.service_type !== 'representacion' && (
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5" style={{color: '#0a3042'}} />
                    {t('delivery_information')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>{t('delivery_address')}</Label>
                      <AddressAutocomplete
                        value={orderForm.delivery_address}
                        onChange={(data) => setOrderForm(prev => ({
                          ...prev,
                          delivery_address: data.address || prev.delivery_address,
                          delivery_lat: data.lat || prev.delivery_lat,
                          delivery_lng: data.lng || prev.delivery_lng
                        }))}
                        placeholder={t('start_typing_address')}
                        dataTestId="delivery-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('contact_name')}</Label>
                      <Input
                        value={orderForm.delivery_contact_name}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_contact_name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('contact_phone')}</Label>
                      <Input
                        type="tel"
                        value={orderForm.delivery_contact_phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9+\s()-]/g, '');
                          setOrderForm(prev => ({ ...prev, delivery_contact_phone: value }));
                        }}
                        placeholder="+34 600 123 456"
                        pattern="[0-9+\s()-]*"
                        required
                      />
                    </div>

                    {/* Toggle for Fixed vs Range Date/Time - Delivery */}
                    <div className="md:col-span-2 flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          {useFixedDeliveryDateTime ? '📅 Fecha y Hora Fija' : '📅 Rango de Fecha y Hora'}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUseFixedDeliveryDateTime(!useFixedDeliveryDateTime)}
                        className="text-xs"
                        style={{borderColor: '#8b5cf6', color: '#8b5cf6'}}
                      >
                        {useFixedDeliveryDateTime ? 'Cambiar a Rango' : 'Cambiar a Fecha Fija'}
                      </Button>
                    </div>

                    {/* Fixed Date/Time Mode - Delivery */}
                    {useFixedDeliveryDateTime ? (
                      <>
                        <div className="space-y-2">
                          <Label>{t('delivery_date_range')}</Label>
                          <DatePicker {...DATE_PICKER_POPPER_PROPS}
                            selected={orderForm.delivery_date_start}
                            onChange={(date) => {
                              setOrderForm(prev => ({ 
                                ...prev, 
                                delivery_date_start: date,
                                delivery_date_end: date
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholderText="Selecciona fecha"
                            dateFormat="dd/MM/yyyy"
                            minDate={orderForm.pickup_date_end ?? orderForm.pickup_date_start ?? new Date()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('delivery_time')}</Label>
                          <Input
                            type="time"
                            value={orderForm.delivery_time_start}
                            onChange={(e) => setOrderForm(prev => ({ 
                              ...prev, 
                              delivery_time_start: e.target.value,
                              delivery_time_end: e.target.value
                            }))}
                            required
                          />
                        </div>
                      </>
                    ) : (
                      /* Range Date/Time Mode - Delivery */
                      <>
                        <div className="space-y-2">
                          <Label>{t('delivery_date_range')}</Label>
                          <DatePicker {...DATE_PICKER_POPPER_PROPS}
                            selectsRange
                            startDate={orderForm.delivery_date_start}
                            endDate={orderForm.delivery_date_end}
                            onChange={(dates) => {
                              const [start, end] = dates;
                              setOrderForm(prev => ({ ...prev, delivery_date_start: start, delivery_date_end: end }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholderText={t('select_dates')}
                            dateFormat="dd/MM/yyyy"
                            minDate={orderForm.pickup_date_end ?? orderForm.pickup_date_start ?? new Date()}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('delivery_time')} - Desde</Label>
                          <Input
                            type="time"
                            value={orderForm.delivery_time_start}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_time_start: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('delivery_time')} - Hasta</Label>
                          <Input
                            type="time"
                            value={orderForm.delivery_time_end}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_time_end: e.target.value }))}
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                )}

                {/* Point C Information - Only for B-C Service */}
                {orderForm.service_type === 'bc_service' && (
                  <div className="space-y-4 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
                    <div className="bg-yellow-100 p-3 rounded-md mb-4">
                      <p className="text-sm text-gray-700">
                        ℹ️ {t('bc_service_note')}
                      </p>
                    </div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-yellow-600" />
                      {t('point_c_information')}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('point_c_address')}</Label>
                        <AddressAutocomplete
                          value={orderForm.point_c_address}
                          onChange={(data) => setOrderForm(prev => ({
                            ...prev,
                            point_c_address: data.address || prev.point_c_address,
                            point_c_lat: data.lat || prev.point_c_lat,
                            point_c_lng: data.lng || prev.point_c_lng
                          }))}
                          placeholder={t('start_typing_address')}
                          dataTestId="point-c-address-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('contact_name')}</Label>
                        <Input
                          value={orderForm.point_c_contact_name}
                          onChange={(e) => setOrderForm(prev => ({ ...prev, point_c_contact_name: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('contact_phone')}</Label>
                        <Input
                          type="tel"
                          value={orderForm.point_c_contact_phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9+\s()-]/g, '');
                            setOrderForm(prev => ({ ...prev, point_c_contact_phone: value }));
                          }}
                          placeholder="+34 600 123 456"
                          pattern="[0-9+\s()-]*"
                          required
                        />
                      </div>

                      {/* Toggle for Fixed vs Range Date/Time - Point C */}
                      <div className="md:col-span-2 flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-300">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700">
                            {useFixedPointCDateTime ? '📅 Fecha y Hora Fija' : '📅 Rango de Fecha y Hora'}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setUseFixedPointCDateTime(!useFixedPointCDateTime)}
                          className="text-xs"
                          style={{borderColor: '#eab308', color: '#ca8a04'}}
                        >
                          {useFixedPointCDateTime ? 'Cambiar a Rango' : 'Cambiar a Fecha Fija'}
                        </Button>
                      </div>

                      {/* Fixed Date/Time Mode - Point C */}
                      {useFixedPointCDateTime ? (
                        <>
                          <div className="space-y-2">
                            <Label>{t('delivery_date_range')}</Label>
                            <DatePicker {...DATE_PICKER_POPPER_PROPS}
                              selected={orderForm.point_c_date_start}
                              onChange={(date) => {
                                setOrderForm(prev => ({ 
                                  ...prev, 
                                  point_c_date_start: date,
                                  point_c_date_end: date
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              placeholderText="Selecciona fecha"
                              dateFormat="dd/MM/yyyy"
                              minDate={orderForm.delivery_date_end || new Date()}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('delivery_time')}</Label>
                            <Input
                              type="time"
                              value={orderForm.point_c_time_start}
                              onChange={(e) => setOrderForm(prev => ({ 
                                ...prev, 
                                point_c_time_start: e.target.value,
                                point_c_time_end: e.target.value
                              }))}
                              required
                            />
                          </div>
                        </>
                      ) : (
                        /* Range Date/Time Mode - Point C */
                        <>
                          <div className="space-y-2">
                            <Label>{t('delivery_date_range')}</Label>
                            <DatePicker {...DATE_PICKER_POPPER_PROPS}
                              selectsRange
                              startDate={orderForm.point_c_date_start}
                              endDate={orderForm.point_c_date_end}
                              onChange={(dates) => {
                                const [start, end] = dates;
                                setOrderForm(prev => ({ ...prev, point_c_date_start: start, point_c_date_end: end }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              placeholderText={t('select_dates')}
                              dateFormat="dd/MM/yyyy"
                              minDate={orderForm.delivery_date_end || new Date()}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('delivery_time')} - Desde</Label>
                            <Input
                              type="time"
                              value={orderForm.point_c_time_start}
                              onChange={(e) => setOrderForm(prev => ({ ...prev, point_c_time_start: e.target.value }))}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('delivery_time')} - Hasta</Label>
                            <Input
                              type="time"
                              value={orderForm.point_c_time_end}
                              onChange={(e) => setOrderForm(prev => ({ ...prev, point_c_time_end: e.target.value }))}
                              required
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Trade-In Note */}
                {orderForm.service_type === 'tradein' && (
                  <div className="bg-blue-100 p-4 rounded-lg border-2 border-blue-300">
                    <p className="text-sm text-gray-700">
                      ℹ️ {t('tradein_note')}
                    </p>
                  </div>
                )}

                <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Car className="w-5 h-5" style={{color: '#36e961'}} />
                    {(orderForm.service_type === 'bc_service' || orderForm.service_type === 'tradein') 
                      ? t('first_vehicle') 
                      : t('vehicle_information')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('vehicle_type')}</Label>
                      <Select value={orderForm.car_type} onValueChange={(val) => setOrderForm(prev => ({ ...prev, car_type: val }))}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('select_vehicle_type')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="car">{t('car')}</SelectItem>
                          <SelectItem value="pickup_truck">{t('pickup_truck')}</SelectItem>
                          <SelectItem value="sport_car">{t('sport_car')}</SelectItem>
                          <SelectItem value="van">{t('van')}</SelectItem>
                          <SelectItem value="motorcycle">{t('motorcycle')}</SelectItem>
                          <SelectItem value="suv">{t('suv')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('brand')}</Label>
                      <Input
                        value={orderForm.car_brand}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, car_brand: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('model')}</Label>
                      <Input
                        value={orderForm.car_model}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, car_model: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('license_plate')}</Label>
                      <Input
                        value={orderForm.car_plate}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, car_plate: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>{t('fuel_type')}</Label>
                      <Select value={orderForm.fuel_type} onValueChange={(val) => setOrderForm(prev => ({ ...prev, fuel_type: val }))}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('select_fuel_type')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="electric">{t('electric')}</SelectItem>
                          <SelectItem value="hybrid">{t('hybrid')}</SelectItem>
                          <SelectItem value="diesel">{t('diesel')}</SelectItem>
                          <SelectItem value="gasoline">{t('gasoline')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Servicios Adicionales y Notas para Primer Vehículo (solo B-C/Trade-In) */}
                  {(orderForm.service_type === 'bc_service' || orderForm.service_type === 'tradein') && (
                    <div className="space-y-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                      <Label className="text-base font-semibold text-blue-900">
                        📋 {t('additional_services')} - {t('first_vehicle')}
                      </Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {additionalServices.map(service => (
                          <div key={service.id + '-first'} className="flex items-start space-x-2 p-3 bg-white border rounded-lg">
                            <Checkbox
                              id={service.id + '-first'}
                              checked={orderForm.first_vehicle_additional_services?.includes(service.id) || false}
                              onCheckedChange={(checked) => {
                                setOrderForm(prev => ({
                                  ...prev,
                                  first_vehicle_additional_services: checked
                                    ? [...(prev.first_vehicle_additional_services || []), service.id]
                                    : (prev.first_vehicle_additional_services || []).filter(id => id !== service.id)
                                }));
                              }}
                            />
                            <label htmlFor={service.id + '-first'} className="flex-1 cursor-pointer">
                              <p className="font-medium text-sm">
                                <span className="text-base mr-2">{getServiceEmoji(service.name)}</span>
                                {translateAdditionalServiceName(service.name, t)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {translateAdditionalServiceDescription(service.name, service.description, t)}
                              </p>
                              <p className="text-sm font-semibold mt-1" style={{color: '#36e961'}}>€{(service.price || 0).toFixed(2)}</p>
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>📝 {t('notes')} - {t('first_vehicle')}</Label>
                        <Textarea
                          value={orderForm.first_vehicle_notes}
                          onChange={(e) => setOrderForm(prev => ({ ...prev, first_vehicle_notes: e.target.value }))}
                          placeholder={t('notes_placeholder')}
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Second Vehicle Section - Mandatory for B-C and Trade-In */}
                {(orderForm.service_type === 'bc_service' || orderForm.service_type === 'tradein') ? (
                  <div className="space-y-4 p-4 bg-orange-50 rounded-lg border-2 border-orange-300">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Car className="w-5 h-5 text-orange-600" />
                      {t('second_vehicle')} *
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('vehicle_type')}</Label>
                        <Select value={orderForm.second_car_type} onValueChange={(val) => setOrderForm(prev => ({ ...prev, second_car_type: val }))}>
                          <SelectTrigger><SelectValue placeholder={t('select_vehicle_type')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="car">{t('car')}</SelectItem>
                            <SelectItem value="pickup_truck">{t('pickup_truck')}</SelectItem>
                            <SelectItem value="sport_car">{t('sport_car')}</SelectItem>
                            <SelectItem value="van">{t('van')}</SelectItem>
                            <SelectItem value="motorcycle">{t('motorcycle')}</SelectItem>
                            <SelectItem value="suv">{t('suv')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('brand')}</Label>
                        <Input 
                          value={orderForm.second_car_brand} 
                          onChange={(e) => setOrderForm(prev => ({ ...prev, second_car_brand: e.target.value }))} 
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('model')}</Label>
                        <Input 
                          value={orderForm.second_car_model} 
                          onChange={(e) => setOrderForm(prev => ({ ...prev, second_car_model: e.target.value }))} 
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('license_plate')}</Label>
                        <Input 
                          value={orderForm.second_car_plate} 
                          onChange={(e) => setOrderForm(prev => ({ ...prev, second_car_plate: e.target.value }))} 
                          required
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('fuel_type')}</Label>
                        <Select value={orderForm.second_fuel_type} onValueChange={(val) => setOrderForm(prev => ({ ...prev, second_fuel_type: val }))}>
                          <SelectTrigger><SelectValue placeholder={t('select_fuel_type')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="electric">{t('electric')}</SelectItem>
                            <SelectItem value="hybrid">{t('hybrid')}</SelectItem>
                            <SelectItem value="diesel">{t('diesel')}</SelectItem>
                            <SelectItem value="gasoline">{t('gasoline')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Servicios Adicionales y Notas para Segundo Vehículo (B-C/Trade-In) */}
                    <div className="mt-4 space-y-4 p-4 bg-purple-50 rounded-lg border-2 border-purple-300">
                      <Label className="text-base font-semibold text-purple-900">
                        📋 {t('additional_services')} - {t('second_vehicle')}
                      </Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {additionalServices.map(service => (
                          <div key={service.id + '-second'} className="flex items-start space-x-2 p-3 bg-white border rounded-lg">
                            <Checkbox
                              id={service.id + '-second'}
                              checked={orderForm.second_vehicle_additional_services?.includes(service.id) || false}
                              onCheckedChange={(checked) => {
                                setOrderForm(prev => ({
                                  ...prev,
                                  second_vehicle_additional_services: checked
                                    ? [...(prev.second_vehicle_additional_services || []), service.id]
                                    : (prev.second_vehicle_additional_services || []).filter(id => id !== service.id)
                                }));
                              }}
                            />
                            <label htmlFor={service.id + '-second'} className="flex-1 cursor-pointer">
                              <p className="font-medium text-sm">
                                <span className="text-base mr-2">{getServiceEmoji(service.name)}</span>
                                {translateAdditionalServiceName(service.name, t)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {translateAdditionalServiceDescription(service.name, service.description, t)}
                              </p>
                              <p className="text-sm font-semibold mt-1" style={{color: '#36e961'}}>€{(service.price || 0).toFixed(2)}</p>
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>📝 {t('notes')} - {t('second_vehicle')}</Label>
                        <Textarea
                          value={orderForm.second_vehicle_notes}
                          onChange={(e) => setOrderForm(prev => ({ ...prev, second_vehicle_notes: e.target.value }))}
                          placeholder={t('notes_placeholder')}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Optional Second Vehicle for Delivery Service */
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">{t('second_vehicle_optional')}</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setOrderForm(prev => ({ ...prev, has_second_vehicle: !prev.has_second_vehicle }))}
                      >
                        {orderForm.has_second_vehicle ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                        {orderForm.has_second_vehicle ? t('remove_second_vehicle') : t('add_second_vehicle')}
                      </Button>
                    </div>
                    {orderForm.has_second_vehicle && (
                      <div className="grid md:grid-cols-2 gap-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                        <div className="space-y-2">
                          <Label>{t('vehicle_type')}</Label>
                          <Select value={orderForm.second_car_type} onValueChange={(val) => setOrderForm(prev => ({ ...prev, second_car_type: val }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="car">{t('car')}</SelectItem>
                              <SelectItem value="pickup_truck">{t('pickup_truck')}</SelectItem>
                              <SelectItem value="sport_car">{t('sport_car')}</SelectItem>
                              <SelectItem value="van">{t('van')}</SelectItem>
                              <SelectItem value="motorcycle">{t('motorcycle')}</SelectItem>
                              <SelectItem value="suv">{t('suv')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('brand')}</Label>
                          <Input value={orderForm.second_car_brand} onChange={(e) => setOrderForm(prev => ({ ...prev, second_car_brand: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('model')}</Label>
                          <Input value={orderForm.second_car_model} onChange={(e) => setOrderForm(prev => ({ ...prev, second_car_model: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('license_plate')}</Label>
                          <Input value={orderForm.second_car_plate} onChange={(e) => setOrderForm(prev => ({ ...prev, second_car_plate: e.target.value }))} />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>{t('fuel_type')}</Label>
                          <Select value={orderForm.second_fuel_type} onValueChange={(val) => setOrderForm(prev => ({ ...prev, second_fuel_type: val }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="electric">{t('electric')}</SelectItem>
                              <SelectItem value="hybrid">{t('hybrid')}</SelectItem>
                              <SelectItem value="diesel">{t('diesel')}</SelectItem>
                              <SelectItem value="gasoline">{t('gasoline')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Campos específicos de Representación */}
                {orderForm.service_type === 'representacion' && (
                  <>
                    {/* Datos del Comprador */}
                    <div className="space-y-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                      <h3 className="font-semibold text-lg text-blue-900">👤 {t('buyer_data')}</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('buyer_name')} *</Label>
                          <Input
                            value={orderForm.buyer_name}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, buyer_name: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('buyer_surname')} *</Label>
                          <Input
                            value={orderForm.buyer_surname}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, buyer_surname: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('buyer_phone')} *</Label>
                          <Input
                            type="tel"
                            value={orderForm.buyer_phone}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, buyer_phone: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('buyer_dni')} *</Label>
                          <Input
                            value={orderForm.buyer_dni}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, buyer_dni: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Datos del Vendedor */}
                    <div className="space-y-4 p-4 bg-orange-50 rounded-lg border-2 border-orange-300">
                      <h3 className="font-semibold text-lg text-orange-900">👤 {t('seller_data')}</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('seller_name')} *</Label>
                          <Input
                            value={orderForm.seller_name}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, seller_name: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller_surname')} *</Label>
                          <Input
                            value={orderForm.seller_surname}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, seller_surname: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller_phone')} *</Label>
                          <Input
                            type="tel"
                            value={orderForm.seller_phone}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, seller_phone: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller_dni')} *</Label>
                          <Input
                            value={orderForm.seller_dni}
                            onChange={(e) => setOrderForm(prev => ({ ...prev, seller_dni: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Servicios Adicionales y Notas - Solo para Delivery */}
                {orderForm.service_type === 'delivery' && (
                  <>
                    <div className="space-y-4">
                      <Label className="text-base font-semibold">{t('additional_services')}</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {additionalServices.map(service => (
                          <div key={service.id} className="flex items-start space-x-2 p-3 border rounded-lg">
                            <Checkbox
                              id={service.id}
                              checked={orderForm.selected_additional_services.includes(service.id)}
                              onCheckedChange={(checked) => {
                                setOrderForm(prev => ({
                                  ...prev,
                                  selected_additional_services: checked
                                    ? [...prev.selected_additional_services, service.id]
                                    : prev.selected_additional_services.filter(id => id !== service.id)
                                }));
                              }}
                            />
                            <label htmlFor={service.id} className="flex-1 cursor-pointer">
                              <p className="font-medium text-sm">
                                <span className="text-base mr-2">{getServiceEmoji(service.name)}</span>
                                {translateAdditionalServiceName(service.name, t)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {translateAdditionalServiceDescription(service.name, service.description, t)}
                              </p>
                              <p className="text-sm font-semibold mt-1" style={{color: '#36e961'}}>€{(service.price || 0).toFixed(2)}</p>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('notes')}</Label>
                      <Textarea
                        value={orderForm.notes}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder={t('notes_placeholder')}
                        rows={3}
                      />
                    </div>
                  </>
                )}

                {editingOrderId && (
                  <ClientOrderDocumentsList
                    orderId={editingOrderId}
                    backendUrl={BACKEND_URL}
                    refreshTrigger={orderClientDocsRefresh}
                    tokenStorageKey="session_token"
                    titleKey="client_order_docs_title"
                    emptyTextKey="client_order_doc_none"
                  />
                )}

                <OrderServiceDocumentsField
                  files={orderAttachmentFiles}
                  onFilesChange={setOrderAttachmentFiles}
                  disabled={submitting}
                  onValidationError={(msg) => toast.error(msg)}
                  labels={{
                    fieldId: 'client-order-create-docs',
                    title: t('service_docs_title'),
                    hint: editingOrderId ? t('service_docs_hint_edit') : t('service_docs_hint'),
                    browse: t('service_docs_browse'),
                    previewTitle: t('service_docs_preview'),
                    previewUnavailableHeic: t('service_docs_preview_unavailable_heic'),
                    remove: t('service_docs_remove'),
                    maxFiles: t('service_docs_max_files'),
                    fileTooLarge: t('service_docs_file_too_large'),
                    invalidType: t('service_docs_invalid_type'),
                  }}
                />

                <div className="space-y-4 bg-amber-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-lg">
                    {orderForm.service_type === 'representacion' ? 'Precio del Servicio' : t('distance_pricing')}
                  </h3>
                  
                  {/* Precio fijo para Representación */}
                  {orderForm.service_type === 'representacion' ? (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Precio Base (sin IVA)</span>
                        <span className="font-bold text-lg">€45.00</span>
                      </div>
                      {isSpanishCompany() && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">IVA (21%)</span>
                            <span className="font-bold text-lg">€{(45 * 0.21).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                            <span className="text-gray-900 font-semibold text-lg">Precio Total (con IVA)</span>
                            <span className="font-bold text-3xl" style={{color: '#8b5cf6'}}>€{(45 * 1.21).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      {!isSpanishCompany() && (
                        <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                          <span className="text-gray-900 font-semibold text-lg">Precio Total</span>
                          <span className="font-bold text-3xl" style={{color: '#8b5cf6'}}>€45.00</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Estado de direcciones - Solo para servicios con transporte */}
                      <div className="mb-4 p-3 bg-white rounded-lg border-2 border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-2">{t('client_address_status')}</p>
                        <div className="flex items-center gap-2 mb-1">
                          {orderForm.pickup_lat ? (
                            <span className="text-green-600">✓ {t('client_pickup_ok')}</span>
                          ) : (
                            <span className="text-red-600">✗ {t('client_pickup_missing')}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {orderForm.delivery_lat ? (
                            <span className="text-green-600">✓ {t('client_delivery_ok')}</span>
                          ) : (
                            <span className="text-red-600">✗ {t('client_delivery_missing')}</span>
                          )}
                        </div>
                      </div>
                      
                      {calculatingDistance ? (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('calculating')}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {orderForm.distance_km && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">{t('calculated_distance')}</span>
                              <span className="font-bold text-lg">{orderForm.distance_km.toFixed(2)} km</span>
                            </div>
                          )}
                          {/* Manual distance input */}
                          <div className="space-y-2 pt-2 border-t border-gray-200">
                            <Label className="text-sm">Establecer distancia manualmente (km)</Label>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Ej: 150.5"
                                value={orderForm.distance_km || ''}
                                onChange={(e) => {
                                  const distance = parseFloat(e.target.value) || 0;
                                  setOrderForm(prev => ({ ...prev, distance_km: distance }));
                                }}
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (orderForm.distance_km > 0) {
                                    setCalculating(true);
                                    try {
                                      let pricingUrl = `${BACKEND_URL}/api/pricing/calculate?distance_km=${orderForm.distance_km}&service_type=${orderForm.service_type}`;
                                      const token = localStorage.getItem('session_token');
                                      const response = await fetch(pricingUrl, {
                                        headers: {
                                          'Authorization': 'Bearer ' + token
                                        }
                                      });
                                      const data = await response.json();
                                      setOrderForm(prev => ({ ...prev, base_price: data.price }));
                                      toast.success('Precio actualizado');
                                    } catch (error) {
                                      console.error('Error calculating price:', error);
                                      toast.error('Error al calcular el precio');
                                    } finally {
                                      setCalculating(false);
                                    }
                                  } else {
                                    toast.error('Introduce una distancia válida');
                                  }
                                }}
                                className="whitespace-nowrap"
                              >
                                Calcular precio
                              </Button>
                            </div>
                          </div>
                          {orderForm.base_price && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">{t('base_price')}</span>
                              <span className="font-bold text-lg">€{orderForm.base_price.toFixed(2)}</span>
                            </div>
                          )}
                          {orderForm.base_price && ((orderForm.service_type === 'bc_service' || orderForm.service_type === 'tradein') 
                            ? ((orderForm.first_vehicle_additional_services || []).length > 0 || (orderForm.second_vehicle_additional_services || []).length > 0)
                            : (orderForm.selected_additional_services || []).length > 0) && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">{t('additional_services_total')}</span>
                              <span className="font-bold text-lg">
                                €{(getTotalPrice() - orderForm.base_price).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {orderForm.base_price && (
                            <>
                              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                                <span className="text-gray-700">{isSpanishCompany() ? 'Subtotal (sin IVA)' : t('total_price')}</span>
                                <span className="font-bold text-lg">
                                  €{getTotalPrice().toFixed(2)}
                                </span>
                              </div>
                              {isSpanishCompany() && (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">IVA (21%)</span>
                                    <span className="font-bold text-lg">€{getVAT().toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between items-center pt-2 border-t-2 border-gray-400">
                                    <span className="text-gray-900 font-semibold text-lg">Total (con IVA)</span>
                                    <span className="font-bold text-2xl" style={{color: '#36e961'}}>
                                      €{getTotalWithVAT().toFixed(2)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowOrderDialog(false)} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 text-white"
                    style={{backgroundColor: '#36e961'}}
                    disabled={
                      submitting ||
                      calculatingDistance ||
                      !orderForm.pickup_lat ||
                      (orderForm.service_type !== 'representacion' && !orderForm.delivery_lat)
                    }
                    data-testid="submit-order-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {editingOrderId ? t('update_order') : t('create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

      {/* Cancellation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">
              {t('cancel_order')}
            </DialogTitle>
            <DialogDescription className="text-left pt-4">
              <div className="space-y-3">
                <p className="font-semibold text-gray-900 text-base">
                  POLÍTICA DE CANCELACIÓN:
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Cancelación dentro de 1 hora de crear el servicio: <strong>GRATIS (0%)</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Cancelación con más de 48h antes del servicio: <strong>GRATIS (0%)</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-600 mr-2">⚠</span>
                    <span>Cancelación entre 24h-48h antes del servicio: <strong>50% del precio</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>Cancelación con menos de 24h antes del servicio: <strong>100% del precio</strong></span>
                  </li>
                </ul>
                <p className="font-semibold text-gray-900 pt-3">
                  ¿Estás seguro que quieres cancelar este servicio?
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false);
                setOrderToCancel(null);
              }}
            >
              No, mantener servicio
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmCancelOrder}
            >
              Sí, cancelar servicio
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para ver fotos del vehículo */}
      {showPhotosDialog && (
        <Dialog open={showPhotosDialog} onOpenChange={setShowPhotosDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Fotos del Vehículo Entregado</DialogTitle>
              {selectedOrderForView && (
                <DialogDescription>
                  {selectedOrderForView.car_brand} {selectedOrderForView.car_model} - {selectedOrderForView.car_plate}
                </DialogDescription>
              )}
            </DialogHeader>

            {loadingPhotos ? (
              <div className="text-center py-8">Cargando fotos...</div>
            ) : vehiclePhotos && vehiclePhotos.pending ? (
              <div className="text-center py-12">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
                  <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg text-yellow-900 mb-2">Fotos Pendientes de Aprobación</h3>
                  <p className="text-sm text-yellow-800 mb-3">{vehiclePhotos.note || 'Las fotos del vehículo estarán disponibles una vez que el administrador las apruebe.'}</p>
                  <p className="text-xs text-yellow-700">Recibirás una notificación cuando estén disponibles.</p>
                </div>
              </div>
            ) : vehiclePhotos && (vehiclePhotos.has_photos || vehiclePhotos.pickup_photos?.length > 0 || vehiclePhotos.delivery_photos?.length > 0 || vehiclePhotos.contract_photos?.length > 0) ? (
              /* Show photos - unified format for both driver and admin uploads */
              <div className="space-y-6">
                {/* Fotos de Recogida */}
                {vehiclePhotos.pickup_photos?.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                      <span>📍</span> Fotos de Recogida ({vehiclePhotos.pickup_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.pickup_photos.map((photo, idx) => {
                        // Handle different photo formats
                        const photoSrc = photo.photo_data || photo.front_photo || (typeof photo === 'string' ? photo : null);
                        if (!photoSrc) return null;
                        return (
                          <img 
                            key={photo.id || idx} 
                            src={photoSrc} 
                            alt={`Recogida ${idx + 1}`} 
                            className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                            onClick={() => window.open(photoSrc, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Entrega */}
                {vehiclePhotos.delivery_photos?.length > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                      <span>🏁</span> Fotos de Entrega ({vehiclePhotos.delivery_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.delivery_photos.map((photo, idx) => {
                        const photoSrc = photo.photo_data || photo.front_photo || (typeof photo === 'string' ? photo : null);
                        if (!photoSrc) return null;
                        return (
                          <img 
                            key={photo.id || idx} 
                            src={photoSrc} 
                            alt={`Entrega ${idx + 1}`} 
                            className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                            onClick={() => window.open(photoSrc, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Fotos del Contrato */}
                {vehiclePhotos.contract_photos?.length > 0 && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                      <span>📄</span> Contrato de Transporte Firmado ({vehiclePhotos.contract_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.contract_photos.map((photo, idx) => {
                        const photoSrc = photo.photo_data || (typeof photo === 'string' ? photo : null);
                        if (!photoSrc) return null;
                        return (
                          <img 
                            key={photo.id || idx} 
                            src={photoSrc} 
                            alt={`Contrato ${idx + 1}`} 
                            className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                            onClick={() => window.open(photoSrc, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Gastos */}
                {vehiclePhotos.expense_photos?.length > 0 && (
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
                      <span>🧾</span> Fotos de Gastos/Tickets ({vehiclePhotos.expense_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.expense_photos.map((photo, idx) => {
                        const photoSrc = photo.photo_data || (typeof photo === 'string' ? photo : null);
                        if (!photoSrc) return null;
                        return (
                          <img 
                            key={photo.id || idx} 
                            src={photoSrc} 
                            alt={`Gasto ${idx + 1}`} 
                            className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                            onClick={() => window.open(photoSrc, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* No photos message */}
                {!vehiclePhotos.pickup_photos?.length && 
                 !vehiclePhotos.delivery_photos?.length &&
                 !vehiclePhotos.contract_photos?.length &&
                 !vehiclePhotos.expense_photos?.length && (
                  <div className="text-center py-8 text-gray-500">
                    No hay fotos disponibles para este servicio.
                  </div>
                )}
              </div>
            ) : vehiclePhotos && vehiclePhotos.has_completion_photos ? (
              /* Show completion photos (from admin) */
              <div className="space-y-6">
                {vehiclePhotos.completion_photos?.pickup_photos?.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                      <span>📍</span> Fotos de Recogida ({vehiclePhotos.completion_photos.pickup_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.completion_photos.pickup_photos.map((url, idx) => (
                        <img 
                          key={idx} 
                          src={`${BACKEND_URL}${url}`} 
                          alt={`Recogida ${idx + 1}`} 
                          className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                          onClick={() => window.open(`${BACKEND_URL}${url}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {vehiclePhotos.completion_photos?.delivery_photos?.length > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                      <span>🏁</span> Fotos de Entrega ({vehiclePhotos.completion_photos.delivery_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.completion_photos.delivery_photos.map((url, idx) => (
                        <img 
                          key={idx} 
                          src={`${BACKEND_URL}${url}`} 
                          alt={`Entrega ${idx + 1}`} 
                          className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                          onClick={() => window.open(`${BACKEND_URL}${url}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {vehiclePhotos.completion_photos?.contract_photos?.length > 0 && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                      <span>📄</span> Contrato de Transporte Firmado ({vehiclePhotos.completion_photos.contract_photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {vehiclePhotos.completion_photos.contract_photos.map((url, idx) => (
                        <img 
                          key={idx} 
                          src={`${BACKEND_URL}${url}`} 
                          alt={`Contrato ${idx + 1}`} 
                          className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-80" 
                          onClick={() => window.open(`${BACKEND_URL}${url}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {!vehiclePhotos.completion_photos?.pickup_photos?.length && 
                 !vehiclePhotos.completion_photos?.delivery_photos?.length &&
                 !vehiclePhotos.completion_photos?.contract_photos?.length && (
                  <div className="text-center py-8 text-gray-500">
                    No hay fotos disponibles para este servicio.
                  </div>
                )}
              </div>
            ) : vehiclePhotos && (vehiclePhotos.vehicle_pickup_photos || vehiclePhotos.vehicle_delivery_photos || vehiclePhotos.pickup_photos || vehiclePhotos.delivery_photos) ? (
              <div className="space-y-8">
                {/* Fotos de Recogida (Pickup) - del conductor */}
                {(vehiclePhotos.vehicle_pickup_photos || vehiclePhotos.pickup_photos) && (
                  <div className="border-2 border-blue-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-blue-600">📸 Fotos de Recogida (Pickup)</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-lg mb-4">Fotos Exteriores</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {(vehiclePhotos.vehicle_pickup_photos || vehiclePhotos.pickup_photos)?.front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Frontal</p>
                              <img src={vehiclePhotos.pickup_photos.front_photo} alt="Frontal" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Trasera</p>
                              <img src={vehiclePhotos.pickup_photos.rear_photo} alt="Trasera" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.left_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Izquierdo Frontal</p>
                              <img src={vehiclePhotos.pickup_photos.left_side_front_photo} alt="Lateral Izq. Frontal" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.left_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Izquierdo Trasero</p>
                              <img src={vehiclePhotos.pickup_photos.left_side_rear_photo} alt="Lateral Izq. Trasero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.right_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Derecho Frontal</p>
                              <img src={vehiclePhotos.pickup_photos.right_side_front_photo} alt="Lateral Der. Frontal" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.right_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Derecho Trasero</p>
                              <img src={vehiclePhotos.pickup_photos.right_side_rear_photo} alt="Lateral Der. Trasero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-lg mb-4">Fotos Interiores</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {vehiclePhotos.pickup_photos.front_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Asientos Delanteros</p>
                              <img src={vehiclePhotos.pickup_photos.front_seats_photo} alt="Asientos Delanteros" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.rear_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Asientos Traseros</p>
                              <img src={vehiclePhotos.pickup_photos.rear_seats_photo} alt="Asientos Traseros" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.dashboard_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Salpicadero</p>
                              <img src={vehiclePhotos.pickup_photos.dashboard_photo} alt="Salpicadero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.pickup_photos.trunk_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Maletero</p>
                              <img src={vehiclePhotos.pickup_photos.trunk_photo} alt="Maletero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      {vehiclePhotos.pickup_photos.delivery_certificate_photo && (
                        <div>
                          <h3 className="font-semibold text-lg mb-4">Acta de Entrega (Pickup)</h3>
                          <img src={vehiclePhotos.pickup_photos.delivery_certificate_photo} alt="Acta de Entrega" className="w-full max-w-2xl mx-auto rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Entrega (Delivery) */}
                {vehiclePhotos.delivery_photos && (
                  <div className="border-2 border-purple-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-purple-600">📸 Fotos de Entrega (Delivery)</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-lg mb-4">Fotos Exteriores</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {vehiclePhotos.delivery_photos.front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Frontal</p>
                              <img src={vehiclePhotos.delivery_photos.front_photo} alt="Frontal" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Trasera</p>
                              <img src={vehiclePhotos.delivery_photos.rear_photo} alt="Trasera" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.left_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Izquierdo Frontal</p>
                              <img src={vehiclePhotos.delivery_photos.left_side_front_photo} alt="Lateral Izq. Frontal" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.left_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Izquierdo Trasero</p>
                              <img src={vehiclePhotos.delivery_photos.left_side_rear_photo} alt="Lateral Izq. Trasero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.right_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Derecho Frontal</p>
                              <img src={vehiclePhotos.delivery_photos.right_side_front_photo} alt="Lateral Der. Frontal" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.right_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Lateral Derecho Trasero</p>
                              <img src={vehiclePhotos.delivery_photos.right_side_rear_photo} alt="Lateral Der. Trasero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-lg mb-4">Fotos Interiores</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {vehiclePhotos.delivery_photos.front_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Asientos Delanteros</p>
                              <img src={vehiclePhotos.delivery_photos.front_seats_photo} alt="Asientos Delanteros" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.rear_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Asientos Traseros</p>
                              <img src={vehiclePhotos.delivery_photos.rear_seats_photo} alt="Asientos Traseros" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.dashboard_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Salpicadero</p>
                              <img src={vehiclePhotos.delivery_photos.dashboard_photo} alt="Salpicadero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {vehiclePhotos.delivery_photos.trunk_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">Maletero</p>
                              <img src={vehiclePhotos.delivery_photos.trunk_photo} alt="Maletero" className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      {vehiclePhotos.delivery_photos.delivery_certificate_photo && (
                        <div>
                          <h3 className="font-semibold text-lg mb-4">Acta de Entrega (Delivery)</h3>
                          <img src={vehiclePhotos.delivery_photos.delivery_certificate_photo} alt="Acta de Entrega" className="w-full max-w-2xl mx-auto rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Completion Photos Section - shown alongside vehicle photos if both exist */}
                {vehiclePhotos.completion_photos && vehiclePhotos.has_completion_photos && (
                  <div className="border-2 border-green-200 rounded-lg p-4 mt-6">
                    <h2 className="text-xl font-bold mb-4 text-green-600">📷 Fotos de Completación del Servicio</h2>
                    <div className="space-y-4">
                      {vehiclePhotos.completion_photos.pickup_photos?.length > 0 && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h3 className="font-medium text-blue-800 mb-2">📍 Fotos de Recogida ({vehiclePhotos.completion_photos.pickup_photos.length})</h3>
                          <div className="grid grid-cols-4 gap-2">
                            {vehiclePhotos.completion_photos.pickup_photos.map((url, idx) => (
                              <a key={idx} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                                <img src={`${BACKEND_URL}${url}`} alt={`Recogida ${idx+1}`} className="w-full h-24 object-cover rounded border hover:opacity-80" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {vehiclePhotos.completion_photos.delivery_photos?.length > 0 && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <h3 className="font-medium text-green-800 mb-2">🏁 Fotos de Entrega ({vehiclePhotos.completion_photos.delivery_photos.length})</h3>
                          <div className="grid grid-cols-4 gap-2">
                            {vehiclePhotos.completion_photos.delivery_photos.map((url, idx) => (
                              <a key={idx} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                                <img src={`${BACKEND_URL}${url}`} alt={`Entrega ${idx+1}`} className="w-full h-24 object-cover rounded border hover:opacity-80" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {vehiclePhotos.completion_photos.expense_photos?.length > 0 && (
                        <div className="bg-yellow-50 p-3 rounded-lg">
                          <h3 className="font-medium text-yellow-800 mb-2">🧾 Fotos de Gastos ({vehiclePhotos.completion_photos.expense_photos.length})</h3>
                          <div className="grid grid-cols-4 gap-2">
                            {vehiclePhotos.completion_photos.expense_photos.map((url, idx) => (
                              <a key={idx} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                                <img src={`${BACKEND_URL}${url}`} alt={`Gasto ${idx+1}`} className="w-full h-24 object-cover rounded border hover:opacity-80" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : vehiclePhotos && vehiclePhotos.completion_photos && vehiclePhotos.has_completion_photos ? (
              /* Show completion photos when no vehicle photos but has completion photos */
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    📷 Fotos subidas al completar el servicio
                  </p>
                </div>
                
                {/* Pickup Completion Photos */}
                {vehiclePhotos.completion_photos.pickup_photos?.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <span>📍</span> Fotos de Recogida ({vehiclePhotos.completion_photos.pickup_photos.length})
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      {vehiclePhotos.completion_photos.pickup_photos.map((url, index) => (
                        <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${BACKEND_URL}${url}`} alt={`Recogida ${index + 1}`} className="w-full h-32 object-cover rounded-lg border hover:opacity-80 transition-opacity" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delivery Completion Photos */}
                {vehiclePhotos.completion_photos.delivery_photos?.length > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <span>🏁</span> Fotos de Entrega ({vehiclePhotos.completion_photos.delivery_photos.length})
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      {vehiclePhotos.completion_photos.delivery_photos.map((url, index) => (
                        <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${BACKEND_URL}${url}`} alt={`Entrega ${index + 1}`} className="w-full h-32 object-cover rounded-lg border hover:opacity-80 transition-opacity" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expense Completion Photos */}
                {vehiclePhotos.completion_photos.expense_photos?.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                      <span>🧾</span> Fotos de Gastos ({vehiclePhotos.completion_photos.expense_photos.length})
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      {vehiclePhotos.completion_photos.expense_photos.map((url, index) => (
                        <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${BACKEND_URL}${url}`} alt={`Gasto ${index + 1}`} className="w-full h-32 object-cover rounded-lg border hover:opacity-80 transition-opacity" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                No hay fotos disponibles para este servicio.
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPhotosDialog(false)}>
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para ver gastos */}
      {showExpensesDialog && (
        <Dialog open={showExpensesDialog} onOpenChange={setShowExpensesDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Gastos del Servicio</DialogTitle>
              {selectedOrderForView && (
                <DialogDescription>
                  {selectedOrderForView.car_brand} {selectedOrderForView.car_model} - {selectedOrderForView.car_plate}
                </DialogDescription>
              )}
            </DialogHeader>

            {loadingExpenses ? (
              <div className="text-center py-8">Cargando gastos...</div>
            ) : orderExpenses && orderExpenses.total_expense !== undefined ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800 mb-4">Gastos Aprobados</p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Gasolina:</span>
                      <span className="font-semibold">€{orderExpenses.fuel_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Peajes:</span>
                      <span className="font-semibold">€{orderExpenses.toll_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Taxi:</span>
                      <span className="font-semibold">€{orderExpenses.taxi_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-green-300">
                      <span className="font-semibold text-gray-900">Total Gastos:</span>
                      <span className="font-bold text-lg text-green-700">€{orderExpenses.total_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>

                {/* Fotos / PDFs de tickets/recibos */}
                {orderExpenses.receipts && orderExpenses.receipts.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Tickets/Recibos</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {orderExpenses.receipts.map((receipt, idx) => {
                        const src =
                          typeof receipt === 'string'
                            ? receipt
                            : receipt?.url || receipt?.data_url || '';
                        const isPdf =
                          typeof src === 'string' &&
                          (src.startsWith('data:application/pdf') ||
                            src.toLowerCase().includes('application/pdf'));
                        if (isPdf) {
                          return (
                            <button
                              key={idx}
                              type="button"
                              className="flex flex-col items-center justify-center h-48 rounded-lg border bg-blue-50 text-blue-800 hover:bg-blue-100"
                              onClick={async () => {
                                const { openExpenseReceipt } = await import('@/utils/expenseDisplay');
                                const ok = await openExpenseReceipt(src);
                                if (!ok) toast.error('No se pudo abrir el PDF');
                              }}
                            >
                              <FileText className="w-10 h-10 mb-2" />
                              <span className="text-sm font-medium">Ver PDF</span>
                              <span className="text-xs text-gray-600 mt-1">Ticket {idx + 1}</span>
                            </button>
                          );
                        }
                        return (
                        <div key={idx} className="relative group">
                          <img 
                            src={src} 
                            alt={`Recibo ${idx + 1}`} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => setEnlargedImage(src)}
                          />
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadFile(src, `recibo-${idx + 1}`);
                              }}
                              className="bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
                              title="Descargar"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                Los gastos aún no han sido reportados o aprobados por el administrador.
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowExpensesDialog(false)}>
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para ampliar imagen/PDF */}
      {enlargedImage && (
        <Dialog open={!!enlargedImage} onOpenChange={() => setEnlargedImage(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                {getFileExtension(enlargedImage) === 'pdf' ? 'Vista de PDF' : 'Vista Ampliada del Ticket'}
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              {getFileExtension(enlargedImage) === 'pdf' ? (
                <iframe
                  src={enlargedImage}
                  className="w-full h-[600px] rounded-lg border"
                  title="PDF Viewer"
                />
              ) : (
                <img 
                  src={enlargedImage} 
                  alt="Ticket ampliado" 
                  className="w-full h-auto rounded-lg"
                />
              )}
              <div className="flex justify-center gap-4 mt-4">
                <Button
                  onClick={() => downloadFile(enlargedImage, 'ticket')}
                  className="flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Descargar {getFileExtension(enlargedImage).toUpperCase()}
                </Button>
                <Button variant="outline" onClick={() => setEnlargedImage(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Completion Details Dialog */}
      {showCompletionDetailsDialog && (
        <Dialog open={showCompletionDetailsDialog} onOpenChange={setShowCompletionDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Historial del Servicio
              </DialogTitle>
              <DialogDescription>
                {selectedOrderForView && (
                  <span>
                    Servicio para <strong>{selectedOrderForView.car_brand} {selectedOrderForView.car_model}</strong> ({selectedOrderForView.car_plate})
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            {loadingCompletionDetails ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : completionDetails ? (
              <div className="space-y-6 py-4">
                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    completionDetails.status === 'delivered' ? 'bg-green-100 text-green-800' :
                    completionDetails.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {completionDetails.status === 'delivered' ? '✅ Entregado' : completionDetails.status}
                  </span>
                  {completionDetails.completed_at && (
                    <span className="text-sm text-gray-500">
                      {t('client_completion_completed_at')} {new Date(completionDetails.completed_at).toLocaleString(dateLocale)}
                    </span>
                  )}
                </div>

                {/* Expenses */}
                {completionDetails.expenses && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">💰 Gastos del Servicio</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {completionDetails.expenses.fuel_expense > 0 && (
                          <div className="flex justify-between">
                            <span>Combustible:</span>
                            <span className="font-medium">€{completionDetails.expenses.fuel_expense.toFixed(2)}</span>
                          </div>
                        )}
                        {completionDetails.expenses.toll_expense > 0 && (
                          <div className="flex justify-between">
                            <span>Peajes:</span>
                            <span className="font-medium">€{completionDetails.expenses.toll_expense.toFixed(2)}</span>
                          </div>
                        )}
                        {completionDetails.expenses.parking_expense > 0 && (
                          <div className="flex justify-between">
                            <span>Aparcamiento:</span>
                            <span className="font-medium">€{completionDetails.expenses.parking_expense.toFixed(2)}</span>
                          </div>
                        )}
                        {completionDetails.expenses.taxi_expense > 0 && (
                          <div className="flex justify-between">
                            <span>Taxi/Transporte:</span>
                            <span className="font-medium">€{completionDetails.expenses.taxi_expense.toFixed(2)}</span>
                          </div>
                        )}
                        {completionDetails.expenses.other_expense > 0 && (
                          <div className="flex justify-between col-span-2">
                            <span>Otros ({completionDetails.expenses.other_expense_description || 'N/A'}):</span>
                            <span className="font-medium">€{completionDetails.expenses.other_expense.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t mt-3 pt-3">
                        <div className="flex justify-between font-semibold">
                          <span>TOTAL:</span>
                          <span>€{completionDetails.expenses.total_expense?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Completion Photos - Organized by Category */}
                {(completionDetails.completion_photos?.pickup_photos?.length > 0 ||
                  completionDetails.completion_photos?.delivery_photos?.length > 0 ||
                  completionDetails.completion_photos?.expense_photos?.length > 0) && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg border-b pb-2">📷 Fotos del Servicio</h3>
                    
                    {/* Pickup Photos */}
                    {completionDetails.completion_photos?.pickup_photos?.length > 0 && (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                          <span>📍</span> Fotos de Recogida ({completionDetails.completion_photos.pickup_photos.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {completionDetails.completion_photos.pickup_photos.map((url, index) => (
                            <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={`${BACKEND_URL}${url}`} alt={`Recogida ${index + 1}`} className="w-full h-24 object-cover rounded border hover:opacity-80 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Delivery Photos */}
                    {completionDetails.completion_photos?.delivery_photos?.length > 0 && (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                          <span>🏁</span> Fotos de Entrega ({completionDetails.completion_photos.delivery_photos.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {completionDetails.completion_photos.delivery_photos.map((url, index) => (
                            <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={`${BACKEND_URL}${url}`} alt={`Entrega ${index + 1}`} className="w-full h-24 object-cover rounded border hover:opacity-80 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Expense Photos */}
                    {completionDetails.completion_photos?.expense_photos?.length > 0 && (
                      <div className="bg-yellow-50 p-3 rounded-lg">
                        <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                          <span>🧾</span> Fotos de Gastos ({completionDetails.completion_photos.expense_photos.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {completionDetails.completion_photos.expense_photos.map((url, index) => (
                            <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={`${BACKEND_URL}${url}`} alt={`Gasto ${index + 1}`} className="w-full h-24 object-cover rounded border hover:opacity-80 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Completion Notes */}
                {completionDetails.completion_notes && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">📝 Notas de Finalización</h3>
                    <p className="bg-gray-50 p-4 rounded-lg text-gray-700">
                      {completionDetails.completion_notes}
                    </p>
                  </div>
                )}

                {/* No data message */}
                {!completionDetails.expenses && 
                 !completionDetails.completion_photos?.pickup_photos?.length && 
                 !completionDetails.completion_photos?.delivery_photos?.length && 
                 !completionDetails.completion_photos?.expense_photos?.length && 
                 !completionDetails.completion_notes && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No hay detalles de finalización disponibles para este servicio.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No se encontraron detalles de finalización.</p>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCompletionDetailsDialog(false)}>
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ClientDashboard;
